;(() => {
  const SECTION_CLASS = 'js-stylist-locator'
  const controllers = new Map()
  const namespace = globalThis.StylistLocator || {}
  const {
    ToastManager,
    loadGoogleMaps,
    map = {},
    search = {},
    filters = {},
    list = {},
    info = {},
    gallery = {},
    api = {},
    geometry = {},
    analytics = {},
  } = namespace

  if (!ToastManager || !loadGoogleMaps) {
    console.warn('Stylist Locator: missing shared utilities, aborting initialization')
    return
  }

  const {
    DEFAULT_CENTER,
    DEFAULT_ZOOM_OUT,
    createMap,
    updateMarkers,
    updateRadiusCircle,
    createUserMarker,
    centerMapOnLocation,
    initializeUserLocation,
    initializeClusterer,
  } = map

  const {
    createStarMesh,
    removeStarMesh,
  } = namespace.clusterMesh || {}

  const {
    setupSearchInputs,
    handlePlaceSelection,
    triggerGeocode,
    hideAutocompleteDropdown,
  } = search

  const {
    setupFilters,
    showPopover,
    setupRadiusSelect,
    hasActiveFilters,
    getRadiusLabel,
  } = filters

  const { renderList } = list
  const {
    getProfileSections,
    renderConsultModal,
    initializeReadMore,
  } = info

  const { initializeGalleries } = gallery
  const { loadStylists, fetchStylistById } = api
  const { parseUrlParams, updateUrl } = namespace.url || {}
  const {
    filterStylistsByRadius,
    filterStylistsByValidRoles,
    filterStylistsByCertification,
    calculateZoomForRadius,
    computeBoundsFromCenter,
    computeDistanceMeters,
    determineRoleCategory,
  } = geometry

  // Phone validation: US (10 digits) or international (+ followed by 7-15 digits)
  function isValidPhone(value) {
    const trimmed = (value || '').trim()
    if (!trimmed) return false
    const digits = trimmed.replace(/\D/g, '')
    // International: starts with + and has 7-15 digits
    if (trimmed.startsWith('+') && digits.length >= 7 && digits.length <= 15) return true
    // US: 10 digits (no country code) or 11 starting with 1
    if (digits.length === 10) return true
    if (digits.length === 11 && digits[0] === '1') return true
    return false
  }

  function showInlineError(input, message) {
    input.classList.add('stylist-locator__form-error')
    let msg = input.nextElementSibling
    if (!msg || !msg.classList.contains('stylist-locator__form-error-msg')) {
      msg = document.createElement('span')
      msg.className = 'stylist-locator__form-error-msg'
      input.parentNode.insertBefore(msg, input.nextSibling)
    }
    msg.textContent = message
    msg.classList.add('stylist-locator__form-error-msg--visible')
  }

  function clearInlineError(input) {
    input.classList.remove('stylist-locator__form-error')
    const msg = input.nextElementSibling
    if (msg && msg.classList.contains('stylist-locator__form-error-msg')) {
      msg.classList.remove('stylist-locator__form-error-msg--visible')
    }
  }

  function attachPhoneBlur(input) {
    input.addEventListener('blur', () => {
      const val = input.value.trim()
      if (val && !isValidPhone(val)) {
        showInlineError(input, 'Enter a valid phone number (e.g., (555) 123-4567 or +44 20 1234 5678)')
      } else {
        clearInlineError(input)
      }
    })
    input.addEventListener('input', () => clearInlineError(input))
  }

  class StylistLocatorController {
    constructor(root) {
      this.root = root
      this.sectionId = root.dataset.sectionId
      this.apiBaseDomain = (() => {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1') {
          return 'http://localhost:3001'
        }
        return root.dataset.apiEndpoint || ''
      })()
      this.googleMapsKey = root.dataset.googleMapsKey
      this.heading = root.dataset.heading || 'Stylist Locator'
      this.defaultRadius = root.dataset.defaultRadius || '25'

      this.state = {
        stylists: [],
        cachedStylists: [],
        isLoading: false,
        selectedStylist: null,
        showRequest: false,
        search: '',
        certification: 'all',
        radius: null,
        radiusCircle: null,
        userCenter: null,
        searchCenter: null,
        windowWidth: window.innerWidth,
        map: null,
        mapContainer: null,
        markers: new Map(),
        geocoder: null,
        autocompleteService: null,
        placesService: null,
        userMarker: null,
        viewMode: 'map',
        referenceCenter: null,
        locationEnabled: false,
        initialRadius: this.defaultRadius,
        initialCertification: 'all',
        initialCenter: null,
        hasInitialized: false,
        radiusIsDefault: true,
        certificationIsDefault: true,
        autocompleteTimeout: null,
        isDropdownOpen: false,
        apiEndpoint: this.buildEndpoint('/api/search-customers-db'),
        markerClusterer: null,
        meshMarkers: null,
        meshMarkerMap: null,
        tempRemovedMarkers: null,
        isClusteringEnabled: null, // Track clustering state to prevent unnecessary marker updates (null = uninitialized)
        openPopover: null, // Track currently open popover
        openPopoverButton: null, // Track which button opened the current popover
        // Filter facets state
        activeSpecializations: [],
        activeServiceOptions: [],
        activeSort: 'best_match',
        // Number of leading entries in `stylists` that match the user's preference
        // filters. `null` means no preference filters are active (no separator).
        matchingCount: null,
      }

      this.refs = this.collectRefs()
      this.toastManager = new ToastManager(this.refs.toastContainer)
      this.galleryCleanups = new Set()
      this.boundResizeHandler = this.handleResize.bind(this)
      this.boundDocumentClick = null

      this.init()
    }

    buildEndpoint(path) {
      if (!this.apiBaseDomain) return ''
      const base = this.apiBaseDomain.replace(/\/$/, '')
      return `${base}${path}`
    }

    getRadiusMiles() {
      return Number(this.state.radius) || Number(this.defaultRadius) || 25
    }

    setSearchInputs(value) {
      this.refs.searchInputs.forEach((input) => {
        input.value = value
      })
    }

    centerMap(coords, radiusMiles = this.getRadiusMiles()) {
      if (!this.state.map || !coords) return
      const zoom = calculateZoomForRadius(
        radiusMiles,
        this.state.map,
        this.state.mapContainer,
        coords
      )
      // Limit zoom-out to 20% beyond the current radius
      const minZoom = calculateZoomForRadius(
        radiusMiles * 1.2,
        this.state.map,
        this.state.mapContainer,
        coords
      )
      this.state.map.setOptions({ minZoom })
      centerMapOnLocation(this.state.map, coords, zoom)
    }

    scheduleMapResize(delay = 100) {
      if (!this.state.map) return
      setTimeout(() => {
        globalThis.google.maps.event.trigger(this.state.map, 'resize')
        this.updateRadiusCircle()
      }, delay)
    }

    clearMeshMarkers() {
      if (!this.state.meshMarkers) return
      if (removeStarMesh) {
        removeStarMesh(this.state.meshMarkers)
      }
      this.state.meshMarkers = null

      // Restore temporarily removed markers (both cluster and standalone)
      if (this.state.tempRemovedMarkers) {
        // Check if we're in clustering mode
        const shouldCluster = this.state.isClusteringEnabled === true ||
          (this.state.map && this.state.map.getZoom() < 24)

        if (shouldCluster && this.state.markerClusterer) {
          // In clustering mode, add markers back to clusterer
          this.state.markerClusterer.addMarkers(this.state.tempRemovedMarkers)
        } else {
          // Not clustering - restore markers directly to map
          this.state.tempRemovedMarkers.forEach((marker) => {
            if (marker && this.state.map) {
              marker.setMap(this.state.map)
            }
          })
        }
        this.state.tempRemovedMarkers = null
      }
    }

    repositionMeshMarkers() {
      const meshData = this.state.meshMarkers
      if (!meshData || !meshData.markers || !meshData.center || !this.state.map) return

      const { calculateStarMeshPositions, calculateMeshRadius } = namespace.clusterMesh || {}
      if (!calculateStarMeshPositions || !calculateMeshRadius) return

      const count = meshData.markers.length
      const radiusPixels = calculateMeshRadius(count)
      const positions = calculateStarMeshPositions(this.state.map, meshData.center, count, radiusPixels)

      if (positions.length !== count) return

      meshData.markers.forEach((marker, i) => {
        if (marker && positions[i]) {
          marker.setPosition(positions[i])
        }
      })

      // Update connecting lines
      if (meshData.lines) {
        meshData.lines.forEach((line, i) => {
          if (line && positions[i]) {
            line.setPath([meshData.center, positions[i]])
          }
        })
      }
    }

    applySearchCenter(coords, searchValue = null) {
      if (searchValue !== null) {
        this.setSearchInputs(searchValue)
        this.state.search = searchValue
      }
      this.state.searchCenter = coords
      this.state.referenceCenter = coords

      this.centerMap(coords)
      this.refreshResults()
      this.syncUrl()
    }

    renderListAndMarkers() {
      this.renderList()
      this.updateMarkers()
    }

    setStylists(stylists, { render = true, matchingCount = null } = {}) {
      this.state.stylists = stylists
      this.state.matchingCount = matchingCount
      if (render) {
        this.renderListAndMarkers()
      }
    }

    clearSelection() {
      this.state.selectedStylist = null
    }

    closeConsultModal({ restoreResults = true } = {}) {
      this._unlockBodyScroll()
      this._removeEscapeClose(this._consultEscHandler)

      if (this.state.showRequest) {
        this.state.showRequest = false
        this.syncUrl()
      }

      if (this.refs.modalOverlay) {
        this.refs.modalOverlay.classList.remove('stylist-locator__modal-overlay--open')
      }
      if (this.refs.modal) {
        // Wait for close transition to finish before clearing content
        const modal = this.refs.modal
        let cleared = false
        const clearContent = () => {
          if (cleared) return
          cleared = true
          const handle = modal.querySelector('[data-element="modal-handle"]')
          modal.innerHTML = ''
          if (handle) modal.appendChild(handle)
        }
        modal.addEventListener('transitionend', function onEnd(e) {
          if (e.target === modal) {
            modal.removeEventListener('transitionend', onEnd)
            clearContent()
          }
        })
        // Fallback in case transitionend doesn't fire (e.g. desktop where no transform transition)
        setTimeout(clearContent, 400)
      }

      // On mobile, return to results drawer with the selected stylist still active
      if (restoreResults && window.innerWidth <= 768 && this.state.selectedStylist && !this._reopeningResultsDrawer) {
        this._reopeningResultsDrawer = true
        this.openResultsDrawer()
        this.updateActiveCard(this.state.selectedStylist.email)
        this._reopeningResultsDrawer = false
      }
    }

    resetSelectionUI({
      closeModal = false,
      renderList = true,
    } = {}) {
      this.clearSelection()
      if (closeModal) {
        this.closeConsultModal()
      }
      if (renderList) {
        this.renderList()
      }
      this.updateActiveCard(null)
      this.updateActiveMarker(null)
      this.restoreCardOrder()
      this.syncUrl()
    }

    showRequestError(message) {
      this.toastManager.show('request', {
        message,
        severity: 'error',
        duration: 3000,
      })
    }

    refreshResults({ updateResetButtons = true } = {}) {
      this.updateRadiusCircle()
      this.fetchStylistsForCurrentRadius()
      this.applyFilters()
      if (updateResetButtons) {
        this.updateResetButtons()
      }
    }

    collectRefs() {
      return {
        heading: this.root.querySelector('[data-element="heading"]'),
        searchInputs: Array.from(this.root.querySelectorAll('[data-element="search-input"]')),
        searchButtons: Array.from(this.root.querySelectorAll('[data-element="search-button"]')),
        searchContainers: Array.from(this.root.querySelectorAll('[data-element="search-container"]')),
        autocompleteDropdowns: Array.from(this.root.querySelectorAll('[data-element="autocomplete-dropdown"]')),
        autocompleteContents: Array.from(this.root.querySelectorAll('[data-element="autocomplete-content"]')),
        // New: certification select dropdown (replaces radio buttons)
        certificationSelect: this.root.querySelector('[data-element="certification-select"]'),
        certificationRadios: [], // Legacy — removed from DOM
        filterInfoButtons: [],
        filterPopovers: [],
        filterPopover: null,
        listCount: this.root.querySelector('[data-element="list-count"]'),
        listCountMobile: null, // Removed — single responsive layout
        listDesktop: this.root.querySelector('[data-element="list-desktop"]'),
        listMobile: null, // Removed — single responsive layout
        viewToggleButtons: [],
        mapHostDesktop: this.root.querySelector('[data-element="map-host-desktop"]'),
        mapHostMobile: null, // Removed — single responsive layout
        mobileLayout: null,
        mobileMapFrame: null,
        modalOverlay: this.root.querySelector('[data-element="modal-overlay"]'),
        modal: this.root.querySelector('[data-element="modal"]'),
        // New: radius slider (replaces dropdown)
        radiusSlider: this.root.querySelector('[data-element="radius-slider"]'),
        radiusValueDisplay: this.root.querySelector('[data-element="radius-value"]'),
        // Legacy radius refs — empty arrays so existing code doesn't break
        radiusSelects: [],
        radiusContainers: [],
        radiusButtons: [],
        radiusDropdowns: [],
        radiusButtonTexts: [],
        resetMapButtons: [],
        toastContainer: this.root.querySelector('[data-element="toast-container"]'),
        filterChips: null,
        // New refs
        controlsSection: this.root.querySelector('[data-element="controls-section"]'),
        filterBar: this.root.querySelector('[data-element="filter-bar"]'),
        sortWrap: this.root.querySelector('[data-element="sort-wrap"]'),
        sortDropdown: this.root.querySelector('[data-element="sort-dropdown"]'),
        sortLabel: this.root.querySelector('[data-element="sort-label"]'),
        sortOptions: this.root.querySelector('[data-element="sort-options"]'),
        // Mobile-specific refs
        filterMobileTrigger: this.root.querySelector('[data-element="filter-mobile-trigger"]'),
        filterActiveChips: this.root.querySelector('[data-element="filter-active-chips"]'),
        showResultsList: this.root.querySelector('[data-element="show-results-list"]'),
        resultsDrawer: this.root.querySelector('[data-element="results-drawer"]'),
        resultsDrawerSort: this.root.querySelector('[data-element="results-drawer-sort"]'),
        resultsDrawerSortWrap: this.root.querySelector('[data-element="results-drawer-sort-wrap"]'),
        resultsDrawerSortLabel: this.root.querySelector('[data-element="results-drawer-sort-label"]'),
        resultsDrawerSortOptions: this.root.querySelector('[data-element="results-drawer-sort-options"]'),
        resultsDrawerOverlay: this.root.querySelector('[data-element="results-drawer-overlay"]'),
        resultsDrawerList: this.root.querySelector('[data-element="results-drawer-list"]'),
        resultsDrawerCount: this.root.querySelector('[data-element="results-drawer-count"]'),
        resultsCountMobile: this.root.querySelector('[data-element="results-count-mobile"]'),
      }
    }

    /* ── Shared drawer utilities ─────────────────────────────── */

    _lockBodyScroll() {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    }

    _unlockBodyScroll() {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }

    _addEscapeClose(closeCallback) {
      const handler = (e) => {
        if (e.key === 'Escape') {
          closeCallback()
          document.removeEventListener('keydown', handler)
        }
      }
      document.addEventListener('keydown', handler)
      return handler
    }

    _removeEscapeClose(handler) {
      if (handler) document.removeEventListener('keydown', handler)
    }

    _closeAllDrawers() {
      // Close filter drawer
      if (this._closeFilterDrawer) this._closeFilterDrawer()
      // Close results drawer
      this.closeResultsDrawer()
      // Close consult modal
      this.closeConsultModal({ restoreResults: false })
      // Close directory modal (separate module, stored on root element)
      if (this.root._directoryModal) this.root._directoryModal.close()
    }

    /* ── Init ─────────────────────────────────────────────────── */

    init() {
      if (!this.googleMapsKey) {
        console.warn('Stylist Locator: Google Maps API key missing')
        return
      }

      // Hero title is now in the Liquid template, no need to set via JS
      // this.refs.heading.textContent = this.heading

      this.setupSearchInputs()
      this.setupRadiusSlider()
      this.setupCertificationSelect()
      this.setupCertCards()
      this.setupFilters()
      this.setupFilterDropdowns()
      this.setupViewToggle()
      this.setupMobileInteractions()
      this.setupSortDropdown()
      this.setupRequestForm()
      this.setupMapReset()
      this.setupResultsListScrollbar()
      this.setupDifferenceVideo()
      this.updateResetButtons()

      const urlState = this.restoreFromUrl()

      window.addEventListener('resize', this.boundResizeHandler)
      this.handleResize()

      loadGoogleMaps(this.googleMapsKey)
        .then((google) => {
          this.state.geocoder = new google.maps.Geocoder()
          if (google.maps.places) {
            this.state.autocompleteService = new google.maps.places.AutocompleteService()
            this.state.placesService = new google.maps.places.PlacesService(document.createElement('div'))
          }
          this.createMap()

          if (urlState && urlState.searchCenter) {
            // URL has coordinates (legacy link format) — skip geolocation, use URL state
            this.state.hasInitialized = true
            this.centerMap(urlState.searchCenter, Number(this.state.radius) || 25)
            const fetchPromise = this.fetchStylistsForCurrentRadius()
            if (fetchPromise) {
              fetchPromise.then(() => this.handlePendingStylistSelection())
            }
          } else if (this._pendingStylistId) {
            // Stylist deep link — `q` here is a person's name, not a geocodable
            // place, so resolve the stylist directly instead of geocoding it.
            this.state.hasInitialized = true
            this.handlePendingStylistSelection()
          } else if (urlState && urlState.search) {
            // Plain location search term — re-geocode it, same as the manual
            // search box does, to restore the map center.
            this.state.hasInitialized = true
            triggerGeocode(urlState.search, this.state.geocoder, {
              onGeocodeSuccess: (coords) => {
                this.applySearchCenter(coords)
              },
              onGeocodeError: () => {
                this.initializeUserLocation()
              },
            })
          } else {
            // No URL coords — normal geolocation flow
            this.initializeUserLocation()
          }

          // Ensure map is properly initialized for mobile after a brief delay
          // This allows the DOM to fully render and ensures the map container has dimensions
          if (this.state.windowWidth <= 768 && this.state.map) {
            setTimeout(() => {
              this.updateMapHost()
            }, 300)
          }

          // Handle browser back/forward navigation
          window.addEventListener('popstate', () => {
            this._isRestoringFromPopstate = true

            const endRestore = () => { this._isRestoringFromPopstate = false }
            const newUrlState = this.restoreFromUrl()

            // Handle stylist deselection: URL has no stylist but state does
            if (!this._pendingStylistId && this.state.selectedStylist) {
              this.resetSelectionUI({ closeModal: true, renderList: true })
            }

            // Handle form close: URL has no form=1 but modal is open
            if (!this._pendingShowRequest && this.state.showRequest) {
              this.state.showRequest = false
              this.closeConsultModal()
            }

            if (newUrlState?.searchCenter) {
              this.centerMap(newUrlState.searchCenter, Number(this.state.radius) || 25)
              const fetchPromise = this.fetchStylistsForCurrentRadius()
              if (fetchPromise) {
                fetchPromise
                  .then(() => this.handlePendingStylistSelection())
                  .finally(endRestore)
              } else {
                endRestore()
              }
            } else if (this._pendingStylistId) {
              this.handlePendingStylistSelection().finally(endRestore)
            } else {
              this.applyFilters()
              endRestore()
            }
          })
        })
        .catch((error) => {
          console.error('Stylist Locator:', error)
          this.toastManager.show('map-error', {
            message: 'Unable to load map. Please try again later.',
            severity: 'error',
            duration: 4000,
          })
        })
    }

    setupSearchInputs() {
      setupSearchInputs(
        this.refs.searchInputs,
        this.refs.searchContainers,
        this.refs.autocompleteDropdowns,
        this.refs.autocompleteContents,
        this.refs.searchButtons,
        this.state,
        {
          onSearchChange: (value) => {
            this.updateResetButtons()
          },
          onPlaceSelect: (placeId) => {
            handlePlaceSelection(placeId, this.state.placesService, {
              onPlaceSelected: (coords, placeName) => {
                this.applySearchCenter(coords, placeName)
                hideAutocompleteDropdown(this.refs.autocompleteDropdowns, this.state)
              },
            })
          },
          onStylistSelect: (emailKey, stylistData) => {
            this.handleStylistSelection(emailKey, stylistData)
          },
          onGeocode: () => {
            triggerGeocode(this.state.search, this.state.geocoder, {
              onGeocodeSuccess: (coords) => {
                this.applySearchCenter(coords)
              },
              onGeocodeError: () => {
                this.toastManager.show('search', {
                  message: 'No address found. Please check the address and try again.',
                  severity: 'error',
                  duration: 3000,
                })
              },
            })
          },
        }
      )
    }

    handleStylistSelection(emailKey, stylistData = null) {
      const { utils = {} } = namespace
      const { decodeKey = (v) => v, parseCoordinate = (v) => v } = utils
      const email = decodeKey(emailKey)

      let stylist = this.state.stylists.find((s) => s.email === email) ||
                     this.state.cachedStylists.find((s) => s.email === email)

      if (!stylist && stylistData) {
        stylist = stylistData
      }

      if (!stylist) {
        console.warn('Stylist not found:', email)
        return
      }

      const searchValue = stylist.name || ''
      this.setSearchInputs(searchValue)
      this.state.search = searchValue

      if (stylist.latitude && stylist.longitude) {
        const coords = {
          lat: parseCoordinate(stylist.latitude),
          lng: parseCoordinate(stylist.longitude),
        }

        this.state.searchCenter = coords
        this.state.referenceCenter = coords

        this.centerMap(coords)

        this.updateRadiusCircle()
        this.fetchStylistsForCurrentRadius().then(() => {
          const foundStylist = this.state.stylists.find((s) => s.email === email) ||
                               this.state.cachedStylists.find((s) => s.email === email)

          if (foundStylist) {
            this.handleListItemClick(emailKey)
            // Check if stylist is in a cluster and expand it
            this.expandClusterIfNeeded(email)
          } else {
            if (!this.state.stylists.find((s) => s.email === email)) {
              this.state.stylists.push(stylist)
              this.updateMarkers()
              this.renderList()
            }
            this.handleListItemClick(emailKey)
            // Check if stylist is in a cluster and expand it
            this.expandClusterIfNeeded(email)
          }
        })
      } else {
        this.handleListItemClick(emailKey)
      }

      hideAutocompleteDropdown(this.refs.autocompleteDropdowns, this.state)
      this.updateResetButtons()
    }

    setupRadiusSelect() {
      setupRadiusSelect(
        this.refs.radiusButtons,
        this.refs.radiusContainers,
        this.refs.radiusDropdowns,
        this.refs.radiusSelects,
        this.refs.radiusButtonTexts,
        this.state,
        {
          onRadiusChange: (value, isDefault) => {
            const center = this.getReferenceCenter()
            if (center && this.state.radius && this.state.map) {
              this.centerMap(center)
            }

            if (center && this.state.radius) {
              this.refreshResults({ updateResetButtons: false })
            } else {
              this.updateRadiusCircle()
              this.state.cachedStylists = []
              this.setStylists([])
            }

            this.updateResetButtons()
          },
        }
      )
    }

    setupRadiusSlider() {
      const slider = this.refs.radiusSlider
      const display = this.refs.radiusValueDisplay
      if (!slider) return

      const stops = (slider.dataset.stops || '10,25,50,100,250,500').split(',').map(Number)
      const snapIndex = (v) => Math.round(parseFloat(v))
      const getMilesFromIndex = (i) => stops[Math.min(Math.max(i, 0), stops.length - 1)]

      // Interpolate miles while dragging between stops
      const getMilesLive = (v) => {
        const fv = parseFloat(v)
        const lo = Math.floor(fv)
        const hi = Math.ceil(fv)
        if (lo === hi || lo < 0 || hi >= stops.length) return getMilesFromIndex(snapIndex(v))
        const t = fv - lo
        return Math.round(stops[lo] + t * (stops[hi] - stops[lo]))
      }

      // Animate slider to target index
      const animateTo = (targetIndex, onDone) => {
        const start = parseFloat(slider.value)
        const end = targetIndex
        if (Math.abs(start - end) < 0.01) { slider.value = end; if (onDone) onDone(); return }
        const duration = 150
        const t0 = performance.now()
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1)
          const ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
          slider.value = start + (end - start) * ease
          if (display) display.textContent = `${getMilesLive(slider.value)} miles`
          if (p < 1) { requestAnimationFrame(tick) } else { slider.value = end; if (onDone) onDone() }
        }
        requestAnimationFrame(tick)
      }

      // Set initial value to the default radius (snap to nearest stop)
      const defaultMiles = Number(this.defaultRadius) || stops[0]
      let defaultIndex = 0
      let bestDiff = Math.abs(stops[0] - defaultMiles)
      for (let i = 1; i < stops.length; i++) {
        const diff = Math.abs(stops[i] - defaultMiles)
        if (diff < bestDiff) { defaultIndex = i; bestDiff = diff }
      }
      slider.value = defaultIndex
      this.state.radius = stops[defaultIndex]
      if (display) display.textContent = `${this.state.radius} miles`
      this.state.radiusIsDefault = true

      slider.addEventListener('input', () => {
        if (display) display.textContent = `${getMilesLive(slider.value)} miles`
      })

      // Make radius stops clickable
      const stopsContainer = slider.closest('.stylist-locator__radius-slider-wrap')?.querySelector('.stylist-locator__radius-stops')
      if (stopsContainer) {
        stopsContainer.querySelectorAll('.stylist-locator__radius-stop').forEach((stop, index) => {
          stop.addEventListener('click', () => {
            const miles = getMilesFromIndex(index)
            animateTo(index, () => {
              if (display) display.textContent = `${miles} miles`
              this.state.radius = miles
              this.state.radiusIsDefault = false
              const center = this.getReferenceCenter()
              if (center && this.state.map) this.centerMap(center)
              if (center) {
                this.refreshResults({ updateResetButtons: false })
              } else {
                this.updateRadiusCircle()
                this.state.cachedStylists = []
                this.setStylists([])
              }
              this.updateResetButtons()
              this.syncUrl()
            })
          })
        })
      }

      let debounceTimer = null
      slider.addEventListener('change', () => {
        const target = snapIndex(slider.value)
        const miles = getMilesFromIndex(target)

        animateTo(target, () => {
          if (display) display.textContent = `${miles} miles`

          clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            this.state.radius = miles
            this.state.radiusIsDefault = false
            const center = this.getReferenceCenter()
            if (center && this.state.map) {
              this.centerMap(center)
            }
            if (center) {
              this.refreshResults({ updateResetButtons: false })
            } else {
              this.updateRadiusCircle()
              this.state.cachedStylists = []
              this.setStylists([])
            }
            this.updateResetButtons()
            this.syncUrl()
          }, 200)
        })
      })
    }

    setupCertificationSelect() {
      const select = this.refs.certificationSelect
      if (!select) return

      // Keep hidden select in sync for backward compat
      select.addEventListener('change', () => {
        const value = select.value
        this.state.certification = value
        this.state.certificationIsDefault = value === 'all'
        this.applyFilters()
        this.updateResetButtons()
        this.syncUrl()
      })

      // Custom dropdown
      const dropdown = this.root.querySelector('[data-element="designation-dropdown"]')
      const trigger = this.root.querySelector('[data-element="designation-trigger"]')
      const textEl = trigger?.querySelector('.stylist-locator__designation-text')
      if (!dropdown || !trigger) return

      trigger.addEventListener('click', () => {
        dropdown.classList.toggle('stylist-locator__designation-dropdown--open')
      })

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-element="designation-dropdown"]')) {
          dropdown.classList.remove('stylist-locator__designation-dropdown--open')
        }
      })

      // Option click
      dropdown.querySelectorAll('.stylist-locator__designation-option').forEach(option => {
        option.addEventListener('click', () => {
          const value = option.dataset.value
          // Update active state
          dropdown.querySelectorAll('.stylist-locator__designation-option').forEach(o => o.classList.remove('stylist-locator__designation-option--active'))
          option.classList.add('stylist-locator__designation-option--active')
          // Update trigger text
          if (textEl) textEl.textContent = option.textContent.trim()
          // Sync hidden select and trigger change
          select.value = value
          select.dispatchEvent(new Event('change', { bubbles: true }))
          // Close dropdown
          dropdown.classList.remove('stylist-locator__designation-dropdown--open')
        })
      })
    }

    setupDifferenceVideo() {
      const video = this.root.querySelector('[data-element="difference-video"]')
      const toggle = this.root.querySelector('[data-element="difference-video-toggle"]')
      if (!video || !toggle) return

      const syncState = () => {
        toggle.dataset.state = video.paused ? 'paused' : 'playing'
      }

      toggle.addEventListener('click', () => {
        if (video.paused) video.play()
        else video.pause()
      })

      video.addEventListener('play', syncState)
      video.addEventListener('pause', syncState)
      syncState()
    }

    setupCertCards() {
      const cards = [...this.root.querySelectorAll('[data-cert-toggle]')]
      const OPEN = 'stylist-locator__cert-card--open'
      const MEASURING = 'stylist-locator__cert-card--measuring'
      const OPEN_MARGIN = 8
      let maxClosed = 0
      let maxOpen = 0

      const isOpen = (c) => c.classList.contains(OPEN)

      const applyMins = () => {
        cards.forEach((c) => {
          const target = isOpen(c) ? maxOpen : maxClosed
          c.style.minHeight = target ? target + 'px' : ''
        })
      }

      const measureCard = (card) => {
        const wasOpen = isOpen(card)
        const prevMin = card.style.minHeight
        card.style.minHeight = ''
        card.classList.add(MEASURING)
        if (wasOpen) {
          card.classList.remove(OPEN)
          void card.offsetHeight
        }

        const closedH = card.offsetHeight
        const inner = card.querySelector('.stylist-locator__cert-card-expanded-inner')
        const innerH = inner ? inner.scrollHeight : 0
        const openByFormula = closedH + innerH + OPEN_MARGIN

        card.classList.add(OPEN)
        void card.offsetHeight
        const openByLayout = card.offsetHeight

        if (!wasOpen) card.classList.remove(OPEN)
        card.classList.remove(MEASURING)
        card.style.minHeight = prevMin

        return { closedH, openH: Math.max(openByFormula, openByLayout) }
      }

      const measureAll = () => {
        let mc = 0
        let mo = 0
        cards.forEach((c) => {
          const { closedH, openH } = measureCard(c)
          if (closedH > mc) mc = closedH
          if (openH > mo) mo = openH
        })
        maxClosed = mc
        maxOpen = mo
      }

      measureAll()
      applyMins()

      cards.forEach((card) => {
        card.addEventListener('click', () => {
          const willBeOpen = !isOpen(card)
          card.classList.toggle(OPEN)
          applyMins()
          const textEl = card.querySelector('.stylist-locator__cert-card-toggle-text')
          if (textEl) textEl.textContent = willBeOpen ? 'Less' : 'More'
        })
      })

      let resizeTimer
      let lastWidth = window.innerWidth
      window.addEventListener('resize', () => {
        // iOS Safari fires resize on every address-bar show/hide while scrolling
        // (height-only changes). Re-measuring then causes the cards to glitch, so
        // only re-measure when the width actually changes.
        if (window.innerWidth === lastWidth) return
        lastWidth = window.innerWidth
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => {
          measureAll()
          applyMins()
        }, 150)
      })
    }

    setupFilters() {
      // Find the correct popover for each button (closest one in the DOM)
      const findPopoverForButton = (button) => {
        // Find the closest filters panel
        const filtersPanel = button.closest('[data-element="filters-panel"]')
        if (!filtersPanel) return this.refs.filterPopover // Fallback to first popover
        
        // Find the popover within the same filters panel
        const popover = filtersPanel.querySelector('[data-element="certification-popover"]')
        return popover || this.refs.filterPopover // Fallback to first popover
      }

      this.boundDocumentClick = setupFilters(
        this.refs.certificationRadios,
        this.refs.filterInfoButtons,
        this.refs.filterPopovers, // Pass all popovers
        {
          onCertificationChange: (value) => {
            this.state.certification = value
            this.state.certificationIsDefault = value === 'all'
            this.applyFilters()
            this.updateResetButtons()
          },
          onInfoButtonClick: (button, certValue) => {
            const popover = findPopoverForButton(button)

            // If the same button is clicked again, close the popover
            if (this.state.openPopoverButton === button && this.state.openPopover) {
              this.state.openPopover.style.display = 'none'
              this.state.openPopover = null
              this.state.openPopoverButton = null
              return
            }

            // Close any currently open popover
            if (this.state.openPopover) {
              this.state.openPopover.style.display = 'none'
            }

            // Open the new popover
            showPopover(button, certValue, popover)
            this.state.openPopover = popover
            this.state.openPopoverButton = button
          },
          onPopoverClose: () => {
            // Reset popover state when closed from outside click
            this.state.openPopover = null
            this.state.openPopoverButton = null
          },
        }
      )
    }

    setupSortDropdown() {
      const {
        sortDropdown,
        sortLabel,
        sortOptions,
        resultsDrawerSort,
        resultsDrawerSortLabel,
        resultsDrawerSortOptions,
      } = this.refs

      const hasDesktop = sortDropdown && sortOptions
      const hasDrawer = resultsDrawerSort && resultsDrawerSortOptions
      if (!hasDesktop && !hasDrawer) return

      const closeAll = () => {
        if (hasDesktop) {
          sortOptions.classList.remove('stylist-locator__sort-options--open')
          sortDropdown.classList.remove('stylist-locator__sort-dropdown--open')
        }
        if (hasDrawer) {
          resultsDrawerSortOptions.classList.remove('stylist-locator__results-drawer-sort-options--open')
          resultsDrawerSort.classList.remove('stylist-locator__results-drawer-sort--open')
        }
      }

      // Shared selection: update state, both labels, both active states, re-apply.
      const selectSort = (sortKey, optionText) => {
        this.state.activeSort = sortKey
        const labelText = `Sort: ${optionText}`
        if (sortLabel) sortLabel.textContent = labelText
        if (resultsDrawerSortLabel) resultsDrawerSortLabel.textContent = labelText

        if (hasDesktop) {
          sortOptions.querySelectorAll('[data-sort]').forEach((o) =>
            o.classList.toggle('stylist-locator__sort-option--active', o.dataset.sort === sortKey)
          )
        }
        if (hasDrawer) {
          resultsDrawerSortOptions.querySelectorAll('[data-sort]').forEach((o) =>
            o.classList.toggle('stylist-locator__results-drawer-sort-option--active', o.dataset.sort === sortKey)
          )
        }

        closeAll()
        this.applyFilters()
        this.syncUrl()
      }

      // Wire desktop dropdown
      if (hasDesktop) {
        sortDropdown.addEventListener('click', (e) => {
          e.stopPropagation()
          const willOpen = !sortOptions.classList.contains('stylist-locator__sort-options--open')
          closeAll()
          if (willOpen) {
            sortOptions.classList.add('stylist-locator__sort-options--open')
            sortDropdown.classList.add('stylist-locator__sort-dropdown--open')
          }
        })

        sortOptions.querySelectorAll('[data-sort]').forEach((option) => {
          option.addEventListener('click', () => {
            selectSort(option.dataset.sort, option.textContent.trim())
          })
        })
      }

      // Wire mobile results-drawer dropdown
      if (hasDrawer) {
        resultsDrawerSort.addEventListener('click', (e) => {
          e.stopPropagation()
          const willOpen = !resultsDrawerSortOptions.classList.contains('stylist-locator__results-drawer-sort-options--open')
          closeAll()
          if (willOpen) {
            resultsDrawerSortOptions.classList.add('stylist-locator__results-drawer-sort-options--open')
            resultsDrawerSort.classList.add('stylist-locator__results-drawer-sort--open')
          }
        })

        resultsDrawerSortOptions.querySelectorAll('[data-sort]').forEach((option) => {
          option.addEventListener('click', () => {
            selectSort(option.dataset.sort, option.textContent.trim())
          })
        })
      }

      // Close on outside click (either menu)
      document.addEventListener('click', (e) => {
        const inDesktop = hasDesktop && (sortDropdown.contains(e.target) || sortOptions.contains(e.target))
        const inDrawer = hasDrawer && (resultsDrawerSort.contains(e.target) || resultsDrawerSortOptions.contains(e.target))
        if (!inDesktop && !inDrawer) closeAll()
      })
    }

    setupFilterDropdowns() {
      const pillWraps = this.root.querySelectorAll('.stylist-locator__filter-pill-wrap')

      const closeAllPills = () => {
        pillWraps.forEach(w => w.classList.remove('stylist-locator__filter-pill-wrap--open'))
      }

      // Toggle dropdown open/close on pill click
      pillWraps.forEach(wrap => {
        const pill = wrap.querySelector('.stylist-locator__filter-pill')
        pill.addEventListener('click', () => {
          const wasOpen = wrap.classList.contains('stylist-locator__filter-pill-wrap--open')
          closeAllPills()
          if (!wasOpen) wrap.classList.add('stylist-locator__filter-pill-wrap--open')
        })
      })

      // Close dropdowns on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.stylist-locator__filter-pill-wrap')) {
          closeAllPills()
        }
      })

      // Close dropdowns on page scroll — the absolutely-positioned popup follows
      // the filter bar and would otherwise pass under/over the sticky header.
      const onScroll = () => {
        const anyOpen = Array.from(pillWraps).some(w => w.classList.contains('stylist-locator__filter-pill-wrap--open'))
        if (anyOpen) closeAllPills()
      }
      window.addEventListener('scroll', onScroll, { passive: true })

      // Chip toggles — only in desktop dropdowns (mobile has its own handler)
      this.root.querySelectorAll('.stylist-locator__filter-dropdown .stylist-locator__filter-chip-toggle').forEach(chip => {
        chip.addEventListener('click', () => {
          chip.classList.toggle('stylist-locator__filter-chip-toggle--active')
        })
      })

      // Apply buttons
      this.root.querySelectorAll('.stylist-locator__filter-dropdown-apply').forEach(btn => {
        btn.addEventListener('click', () => {
          this.collectFacetFilters()
          this.applyFilters()
          this.updateFilterPillCounts()
          this.renderFacetChips()
          this.syncUrl()
          // Close dropdown
          btn.closest('.stylist-locator__filter-pill-wrap')?.classList.remove('stylist-locator__filter-pill-wrap--open')
        })
      })

      // Clear buttons
      this.root.querySelectorAll('.stylist-locator__filter-dropdown-clear').forEach(btn => {
        btn.addEventListener('click', () => {
          const dropdown = btn.closest('.stylist-locator__filter-dropdown')
          dropdown.querySelectorAll('.stylist-locator__filter-chip-toggle--active').forEach(c => c.classList.remove('stylist-locator__filter-chip-toggle--active'))
          dropdown.querySelectorAll('.stylist-locator__filter-toggle-input').forEach(t => { t.checked = false })
        })
      })
    }


    collectFacetFilters() {
      // Specializations
      const specDropdown = this.root.querySelector('[data-filter-dropdown="specialization"]')
      this.state.activeSpecializations = specDropdown
        ? Array.from(specDropdown.querySelectorAll('.stylist-locator__filter-chip-toggle--active')).map(c => c.dataset.value)
        : []

      // Service options
      const svcDropdown = this.root.querySelector('[data-filter-dropdown="service-options"]')
      this.state.activeServiceOptions = svcDropdown
        ? Array.from(svcDropdown.querySelectorAll('.stylist-locator__filter-toggle-input:checked')).map(t => t.dataset.value)
        : []
    }

    updateFilterPillCounts() {
      const counts = {
        'specialization': this.state.activeSpecializations.length,
        'service-options': this.state.activeServiceOptions.length,
      }
      this.root.querySelectorAll('.stylist-locator__filter-pill-wrap').forEach(wrap => {
        const group = wrap.dataset.filterGroup
        const countEl = wrap.querySelector('.stylist-locator__filter-pill-count')
        const count = counts[group] || 0
        if (countEl) {
          countEl.textContent = count
          countEl.classList.toggle('stylist-locator__filter-pill-count--visible', count > 0)
        }
      })
    }

    renderFacetChips() {
      const chips = []
      const labelMap = {}
      // Only collect from desktop dropdowns to avoid duplicates with mobile drawer
      this.root.querySelectorAll('.stylist-locator__filter-dropdown .stylist-locator__filter-chip-toggle--active, .stylist-locator__filter-dropdown .stylist-locator__filter-toggle-input:checked').forEach(el => {
        const value = el.dataset.value
        const label = el.textContent?.trim() || el.closest('label')?.querySelector('span')?.textContent?.trim() || value
        labelMap[value] = label
        chips.push({ value, label })
      })

      if (!this.refs.filterActiveChips) return
      if (chips.length === 0) {
        this.refs.filterActiveChips.innerHTML = ''
        return
      }

      this.refs.filterActiveChips.innerHTML = chips.map(c => `
        <div class="stylist-locator__active-filter-pill" data-filter-value="${c.value}">
          <span>${c.label}</span>
          <button type="button" class="stylist-locator__active-filter-pill-close" data-filter-value="${c.value}" aria-label="Remove ${c.label}">
            <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 1L6 6M6 1L1 6" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>
          </button>
        </div>
      `).join('') + '<button type="button" class="stylist-locator__active-filter-clear-all">Clear all</button>'

      // Wire up chip removal
      this.refs.filterActiveChips.querySelectorAll('.stylist-locator__active-filter-pill-close').forEach(btn => {
        btn.addEventListener('click', () => {
          const value = btn.dataset.filterValue
          const chipToggle = this.root.querySelector(`.stylist-locator__filter-chip-toggle[data-value="${value}"]`)
          if (chipToggle) chipToggle.classList.remove('stylist-locator__filter-chip-toggle--active')
          const toggleInput = this.root.querySelector(`.stylist-locator__filter-toggle-input[data-value="${value}"]`)
          if (toggleInput) toggleInput.checked = false
          // Re-collect and apply
          this.collectFacetFilters()
          this.applyFilters()
          this.updateFilterPillCounts()
          this.renderFacetChips()
          this.syncUrl()
        })
      })

      const clearAll = this.refs.filterActiveChips.querySelector('.stylist-locator__active-filter-clear-all')
      if (clearAll) {
        clearAll.addEventListener('click', () => {
          this.root.querySelectorAll('.stylist-locator__filter-chip-toggle--active').forEach(c => c.classList.remove('stylist-locator__filter-chip-toggle--active'))
          this.root.querySelectorAll('.stylist-locator__filter-toggle-input:checked').forEach(t => { t.checked = false })
          this.collectFacetFilters()
          this.applyFilters()
          this.updateFilterPillCounts()
          this.renderFacetChips()
          this.syncUrl()
        })
      }
    }

    // Sync mobile filter drawer state from current desktop state when opening
    syncMobileFilterState() {
      const drawer = this.root.querySelector('[data-element="filter-drawer"]')
      if (!drawer) return

      // Sync specializations
      this.state.activeSpecializations.forEach(val => {
        const chip = drawer.querySelector(`.stylist-locator__filter-chip-toggle[data-value="${val}"][data-mobile-group="specialization"]`)
        if (chip) chip.classList.add('stylist-locator__filter-chip-toggle--active')
      })

      // Sync service options
      this.state.activeServiceOptions.forEach(val => {
        const toggle = drawer.querySelector(`.stylist-locator__filter-toggle-input[data-value="${val}"][data-mobile-group="service-options"]`)
        if (toggle) toggle.checked = true
      })

      this.updateMobileFilterCounts()
    }

    // Collect filters from mobile drawer into state
    collectMobileFilters() {
      const drawer = this.root.querySelector('[data-element="filter-drawer"]')
      if (!drawer) return

      this.state.activeSpecializations = Array.from(
        drawer.querySelectorAll('.stylist-locator__filter-chip-toggle--active[data-mobile-group="specialization"]')
      ).map(c => c.dataset.value)

      this.state.activeServiceOptions = Array.from(
        drawer.querySelectorAll('.stylist-locator__filter-toggle-input:checked[data-mobile-group="service-options"]')
      ).map(t => t.dataset.value)
    }

    // Sync desktop dropdown state from current state (after mobile apply)
    syncDesktopFilterState() {
      // Sync specialization chips
      this.root.querySelectorAll('[data-filter-dropdown="specialization"] .stylist-locator__filter-chip-toggle').forEach(chip => {
        chip.classList.toggle('stylist-locator__filter-chip-toggle--active', this.state.activeSpecializations.includes(chip.dataset.value))
      })
      // Sync service option toggles
      this.root.querySelectorAll('[data-filter-dropdown="service-options"] .stylist-locator__filter-toggle-input').forEach(t => {
        t.checked = this.state.activeServiceOptions.includes(t.dataset.value)
      })
    }

    // Update mobile accordion count badges
    updateMobileFilterCounts() {
      const drawer = this.root.querySelector('[data-element="filter-drawer"]')
      if (!drawer) return

      const groups = { 'specialization': 0, 'service-options': 0 }
      drawer.querySelectorAll('.stylist-locator__filter-chip-toggle--active[data-mobile-group]').forEach(c => {
        groups[c.dataset.mobileGroup] = (groups[c.dataset.mobileGroup] || 0) + 1
      })
      drawer.querySelectorAll('.stylist-locator__filter-toggle-input:checked[data-mobile-group]').forEach(t => {
        groups[t.dataset.mobileGroup] = (groups[t.dataset.mobileGroup] || 0) + 1
      })

      drawer.querySelectorAll('.stylist-locator__filter-accordion').forEach(acc => {
        const group = acc.dataset.mobileFilter
        const count = groups[group] || 0
        const countEl = acc.querySelector('.stylist-locator__filter-accordion-count')
        if (countEl) {
          countEl.textContent = count
          countEl.classList.toggle('stylist-locator__filter-accordion-count--visible', count > 0)
        }
      })

      // Update the "Show X Results" button text
      const applyBtn = this.root.querySelector('[data-element="filter-drawer-apply"]')
      if (applyBtn) {
        const total = this.state.stylists?.length || 0
        applyBtn.textContent = `Show ${total} Results`
      }
    }

    setupViewToggle() {
      this.refs.viewToggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.view
          if (view && view !== this.state.viewMode) {
            this.state.viewMode = view
            this.renderViewToggle()
            this.renderList()
            this.updateMapHost()
            
            // Trigger map resize when switching to map view to ensure proper rendering
            if (view === 'map' && this.state.map) {
              this.scheduleMapResize(150)
            }
          }
        })
      })
      
      // Set initial view mode on mobile layout
      if (this.refs.mobileLayout) {
        this.refs.mobileLayout.dataset.viewMode = this.state.viewMode
      }
      this.renderViewToggle()
      
      // Ensure map is properly initialized for mobile on first load
      if (this.state.windowWidth <= 768 && this.state.map) {
        setTimeout(() => {
          this.updateMapHost()
        }, 200)
      }
    }

    setupMobileInteractions() {
      // "DETAILED FILTERS" mobile trigger → open mobile filter drawer
      const filterDrawer = this.root.querySelector('[data-element="filter-drawer"]')
      const filterDrawerOverlay = this.root.querySelector('[data-element="filter-drawer-overlay"]')

      // Close mobile filter drawer
      const closeFilterDrawer = () => {
        if (filterDrawer) filterDrawer.classList.remove('stylist-locator__filter-drawer--open')
        if (filterDrawerOverlay) filterDrawerOverlay.classList.remove('stylist-locator__filter-drawer-overlay--visible')
        this._unlockBodyScroll()
        this._removeEscapeClose(this._filterDrawerEscHandler)
      }
      this._closeFilterDrawer = closeFilterDrawer

      if (this.refs.filterMobileTrigger && filterDrawer) {
        this.refs.filterMobileTrigger.addEventListener('click', () => {
          this._closeAllDrawers()
          this.syncMobileFilterState()
          filterDrawer.classList.add('stylist-locator__filter-drawer--open')
          if (filterDrawerOverlay) filterDrawerOverlay.classList.add('stylist-locator__filter-drawer-overlay--visible')
          this._lockBodyScroll()
          this._filterDrawerEscHandler = this._addEscapeClose(closeFilterDrawer)
        })
      }

      // Drag anywhere on the sheet to dismiss filter drawer (app-style bottom sheet)
      if (filterDrawer && namespace.utils?.attachSheetDrag) {
        namespace.utils.attachSheetDrag(filterDrawer, closeFilterDrawer)
      }
      if (filterDrawerOverlay) filterDrawerOverlay.addEventListener('click', closeFilterDrawer)

      // Accordion toggles
      this.root.querySelectorAll('.stylist-locator__filter-accordion-header').forEach(header => {
        header.addEventListener('click', () => {
          header.closest('.stylist-locator__filter-accordion').classList.toggle('stylist-locator__filter-accordion--open')
        })
      })

      // Mobile chip toggles
      filterDrawer?.querySelectorAll('.stylist-locator__filter-chip-toggle').forEach(chip => {
        chip.addEventListener('click', () => chip.classList.toggle('stylist-locator__filter-chip-toggle--active'))
      })

      // Mobile filter drawer Apply button
      const filterDrawerApply = this.root.querySelector('[data-element="filter-drawer-apply"]')
      if (filterDrawerApply) {
        filterDrawerApply.addEventListener('click', () => {
          this.collectMobileFilters()
          this.applyFilters()
          this.syncDesktopFilterState()
          this.updateFilterPillCounts()
          this.renderFacetChips()
          this.updateMobileFilterCounts()
          this.syncUrl()
          closeFilterDrawer()
        })
      }

      // Mobile filter drawer Clear all
      const filterDrawerClear = this.root.querySelector('[data-element="filter-drawer-clear"]')
      if (filterDrawerClear) {
        filterDrawerClear.addEventListener('click', () => {
          filterDrawer.querySelectorAll('.stylist-locator__filter-chip-toggle--active').forEach(c => c.classList.remove('stylist-locator__filter-chip-toggle--active'))
          filterDrawer.querySelectorAll('.stylist-locator__filter-toggle-input:checked').forEach(t => { t.checked = false })
          this.updateMobileFilterCounts()
        })
      }

      // "Show Results List" → open results drawer
      if (this.refs.showResultsList) {
        this.refs.showResultsList.addEventListener('click', () => {
          this.openResultsDrawer()
        })
      }

      // Close results drawer via overlay
      if (this.refs.resultsDrawerOverlay) {
        this.refs.resultsDrawerOverlay.addEventListener('click', () => {
          this.closeResultsDrawer()
        })
      }

      // Drag anywhere on the sheet to dismiss results drawer (app-style bottom sheet)
      if (this.refs.resultsDrawer && namespace.utils?.attachSheetDrag) {
        namespace.utils.attachSheetDrag(this.refs.resultsDrawer, () => this.closeResultsDrawer())
      }
    }

    openResultsDrawer() {
      if (!this.refs.resultsDrawer) return
      this._closeAllDrawers()
      // Clone list content into drawer
      if (this.refs.listDesktop && this.refs.resultsDrawerList) {
        this.refs.resultsDrawerList.innerHTML = this.refs.listDesktop.innerHTML

        // Move selected stylist card to top so it's visible without scrolling
        if (this.state.selectedStylist) {
          const { encodeKey = (v) => v } = namespace.utils || {}
          const emailKey = encodeKey(this.state.selectedStylist.email)
          const activeCard = this.refs.resultsDrawerList.querySelector(`[data-stylist-email="${emailKey}"]`)
          if (activeCard) {
            this.refs.resultsDrawerList.prepend(activeCard)
            this.refs.resultsDrawerList.scrollTop = 0
          }
        }

        // Re-attach click handlers to cloned buttons and cards
        this.refs.resultsDrawerList.querySelectorAll('[data-action="book"]').forEach(el => {
          el.addEventListener('click', (e) => {
            e.stopPropagation()
            this.handleListItemClick(el.dataset.stylistEmail)
          })
        })
        this.refs.resultsDrawerList.querySelectorAll('.stylist-locator__profile-card').forEach(card => {
          card.style.cursor = 'pointer'
          card.addEventListener('click', () => {
            this.handleCardClick(card.dataset.stylistEmail)
          })
        })
        // Setup "Learn More" handlers on cloned cards
        const { list: listModule = {} } = namespace
        if (listModule.setupLearnMoreHandlers) {
          listModule.setupLearnMoreHandlers(this.refs.resultsDrawerList)
        }
      }
      // Sync count
      if (this.refs.listCount && this.refs.resultsDrawerCount) {
        this.refs.resultsDrawerCount.innerHTML = this.refs.listCount.innerHTML
      }
      this.refs.resultsDrawer.classList.add('stylist-locator__results-drawer--open')
      if (this.refs.resultsDrawerOverlay) {
        this.refs.resultsDrawerOverlay.classList.add('stylist-locator__results-drawer-overlay--visible')
      }
      // Use overflow:hidden instead of position:fixed to prevent the page shift
      document.body.style.overflow = 'hidden'
      this._resultsDrawerEscHandler = this._addEscapeClose(() => this.closeResultsDrawer())
      this.setupDrawerScrollbar()
    }

    setupDrawerScrollbar() {
      const list = this.refs.resultsDrawerList
      const thumb = this.root.querySelector('[data-element="results-drawer-scrollbar-thumb"]')
      const track = this.root.querySelector('.stylist-locator__results-drawer-scrollbar-track')
      if (!list || !thumb || !track) return

      let thumbRafId = null
      const updateThumb = () => {
        if (thumbRafId) return
        thumbRafId = requestAnimationFrame(() => {
          thumbRafId = null
          const { scrollTop, scrollHeight, clientHeight } = list
          if (scrollHeight <= clientHeight) {
            thumb.style.display = 'none'
            return
          }
          thumb.style.display = 'block'
          const trackHeight = track.offsetHeight
          const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight)
          const maxScroll = scrollHeight - clientHeight
          const maxThumbTop = trackHeight - thumbHeight
          const thumbTop = maxScroll > 0 ? (scrollTop / maxScroll) * maxThumbTop : 0
          thumb.style.height = thumbHeight + 'px'
          thumb.style.top = thumbTop + 'px'
        })
      }

      // Remove old listener if any
      if (this._drawerScrollHandler) {
        list.removeEventListener('scroll', this._drawerScrollHandler)
      }
      this._drawerScrollHandler = updateThumb
      list.addEventListener('scroll', updateThumb, { passive: true })

      // Initial update after transition settles
      setTimeout(updateThumb, 400)
    }

    setupResultsListScrollbar() {
      const list = this.root.querySelector('.stylist-locator__results-list-wrap')
      const thumb = this.root.querySelector('[data-element="results-list-scrollbar-thumb"]')
      const track = this.root.querySelector('.stylist-locator__results-list-scrollbar-track')
      if (!list || !thumb || !track) return

      let thumbRafId = null
      const updateThumb = () => {
        if (thumbRafId) return
        thumbRafId = requestAnimationFrame(() => {
          thumbRafId = null
          const { scrollTop, scrollHeight, clientHeight } = list
          if (scrollHeight <= clientHeight) {
            thumb.style.display = 'none'
            return
          }
          thumb.style.display = 'block'
          const trackHeight = track.offsetHeight
          const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight)
          const maxScroll = scrollHeight - clientHeight
          const maxThumbTop = trackHeight - thumbHeight
          const thumbTop = maxScroll > 0 ? (scrollTop / maxScroll) * maxThumbTop : 0
          thumb.style.height = thumbHeight + 'px'
          thumb.style.top = thumbTop + 'px'
        })
      }

      if (this._resultsListScrollHandler) {
        list.removeEventListener('scroll', this._resultsListScrollHandler)
      }
      this._resultsListScrollHandler = updateThumb
      this._updateResultsListThumb = updateThumb
      list.addEventListener('scroll', updateThumb, { passive: true })

      updateThumb()
    }

    closeResultsDrawer() {
      if (this.refs.resultsDrawer) {
        this.refs.resultsDrawer.classList.remove('stylist-locator__results-drawer--open')
      }
      if (this.refs.resultsDrawerOverlay) {
        this.refs.resultsDrawerOverlay.classList.remove('stylist-locator__results-drawer-overlay--visible')
      }
      document.body.style.overflow = ''
      this._removeEscapeClose(this._resultsDrawerEscHandler)
    }

    setupRequestForm() {
      // Form is now handled by the consult modal — no setup needed here
    }

    setupMapReset() {
      this.refs.resetMapButtons.forEach((button) => {
        button.addEventListener('click', () => this.resetMapView())
      })
    }

    resetMapView() {
      if (this.state.locationEnabled && this.state.initialCenter) {
        this.state.search = ''
        this.state.searchCenter = null
        this.state.referenceCenter = { ...this.state.initialCenter }
        this.state.radius = this.state.initialRadius
        this.state.radiusIsDefault = true
        this.state.certification = this.state.initialCertification
        this.state.certificationIsDefault = true

        if (this.state.map) {
          this.centerMap(this.state.initialCenter, Number(this.state.radius))
        }

        this.refreshResults({ updateResetButtons: false })
      } else {
        this.state.search = ''
        this.state.searchCenter = null
        this.state.referenceCenter = null
        this.state.radius = null
        this.state.radiusIsDefault = true
        this.state.certification = 'all'
        this.state.certificationIsDefault = true

        if (this.state.map) {
          this.state.map.setOptions({ minZoom: null })
          this.state.map.setCenter(DEFAULT_CENTER)
          this.state.map.setZoom(DEFAULT_ZOOM_OUT)
        }

        if (this.state.radiusCircle) {
          this.state.radiusCircle.setMap(null)
          this.state.radiusCircle = null
        }

        this.state.cachedStylists = []
        this.setStylists([])
      }

      this.clearSelection()

      this.setSearchInputs('')
      this.refs.certificationRadios.forEach((radio) => {
        radio.checked = radio.value === 'all'
      })

      this.refs.radiusSelects.forEach((select) => {
        select.selectedIndex = 0
      })

      this.updateResetButtons()
      this.syncUrl()
    }

    // ========================================================================
    // URL State Sync
    // ========================================================================

    syncUrl(usePushState = false) {
      if (this._isRestoringFromPopstate) return
      if (updateUrl) updateUrl(this.state, usePushState)
    }

    restoreFromUrl() {
      if (!parseUrlParams) return null
      const urlState = parseUrlParams()
      if (!urlState) return null

      // Search
      if (urlState.search != null) {
        this.state.search = urlState.search
        this.setSearchInputs(urlState.search)
      }

      if (urlState.searchCenter) {
        this.state.searchCenter = urlState.searchCenter
        this.state.referenceCenter = urlState.searchCenter
      }

      // Radius — snap to the nearest configured stop so the slider thumb,
      // the radius label, and the map circle all agree, even when the URL
      // carries a legacy value (e.g. r=125 from before the stops changed).
      if (urlState.radius != null) {
        this.state.radiusIsDefault = false
        if (this.refs.radiusSlider) {
          const stops = (this.refs.radiusSlider.dataset.stops || '10,25,50,100,250,500').split(',').map(Number)
          let snapIndex = 0
          let bestDiff = Math.abs(stops[0] - urlState.radius)
          for (let i = 1; i < stops.length; i++) {
            const diff = Math.abs(stops[i] - urlState.radius)
            if (diff < bestDiff) { snapIndex = i; bestDiff = diff }
          }
          this.state.radius = stops[snapIndex]
          this.refs.radiusSlider.value = snapIndex
          if (this.refs.radiusValueDisplay) {
            this.refs.radiusValueDisplay.textContent = `${this.state.radius} miles`
          }
        } else {
          this.state.radius = urlState.radius
        }
      }

      // Certification
      if (urlState.certification) {
        this.state.certification = urlState.certification
        this.state.certificationIsDefault = urlState.certification === 'all'
        const select = this.refs.certificationSelect
        if (select) select.value = urlState.certification
        const dropdown = this.root.querySelector('[data-element="designation-dropdown"]')
        if (dropdown) {
          const option = dropdown.querySelector(`.stylist-locator__designation-option[data-value="${urlState.certification}"]`)
          if (option) {
            dropdown.querySelectorAll('.stylist-locator__designation-option').forEach(o =>
              o.classList.remove('stylist-locator__designation-option--active')
            )
            option.classList.add('stylist-locator__designation-option--active')
            const textEl = this.root.querySelector('[data-element="designation-trigger"]')?.querySelector('.stylist-locator__designation-text')
            if (textEl) textEl.textContent = option.textContent.trim()
          }
        }
      }

      // Sort
      if (urlState.activeSort) {
        this.state.activeSort = urlState.activeSort
        const { sortLabel, sortOptions, resultsDrawerSortLabel, resultsDrawerSortOptions } = this.refs
        const sortKey = urlState.activeSort
        let labelText = null

        if (sortOptions) {
          const option = sortOptions.querySelector(`[data-sort="${sortKey}"]`)
          if (option) {
            sortOptions.querySelectorAll('[data-sort]').forEach(o =>
              o.classList.toggle('stylist-locator__sort-option--active', o === option)
            )
            labelText = `Sort: ${option.textContent.trim()}`
            if (sortLabel) sortLabel.textContent = labelText
          }
        }
        if (resultsDrawerSortOptions) {
          const option = resultsDrawerSortOptions.querySelector(`[data-sort="${sortKey}"]`)
          if (option) {
            resultsDrawerSortOptions.querySelectorAll('[data-sort]').forEach(o =>
              o.classList.toggle('stylist-locator__results-drawer-sort-option--active', o === option)
            )
            if (!labelText) labelText = `Sort: ${option.textContent.trim()}`
            if (resultsDrawerSortLabel) resultsDrawerSortLabel.textContent = labelText
          }
        }
      }

      // Facet filters
      if (urlState.activeSpecializations) {
        this.state.activeSpecializations = urlState.activeSpecializations
      }
      if (urlState.activeServiceOptions) {
        this.state.activeServiceOptions = urlState.activeServiceOptions
      }

      // Sync filter UI
      this.syncDesktopFilterState()
      this.updateFilterPillCounts()
      this.renderFacetChips()

      this._pendingStylistId = urlState.selectedStylistId || null
      this._pendingShowRequest = urlState.showRequest || false

      return urlState
    }

    async handlePendingStylistSelection() {
      const id = this._pendingStylistId
      if (!id) return
      this._pendingStylistId = null

      let stylist = this.state.stylists.find(s => s.id === id) ||
                    this.state.cachedStylists.find(s => s.id === id)

      // Deep-link fallback: fetch from API if not in current results
      if (!stylist && fetchStylistById) {
        stylist = await fetchStylistById(this.apiBaseDomain, id)
        if (stylist) {
          this.state.cachedStylists.push(stylist)

          // Auto-center map on stylist's location if no search center is set
          if (!this.state.searchCenter && stylist.latitude && stylist.longitude) {
            const coords = { lat: stylist.latitude, lng: stylist.longitude }
            this.state.searchCenter = coords
            this.state.referenceCenter = coords
            this.centerMap(coords)
            this.refreshResults()
          }
        }
      }

      if (!stylist) return

      this.trackStylistView(stylist, 'deep_link')
      this.state.selectedStylist = stylist
      this.updateActiveCard(stylist.email)
      this.updateActiveMarker(stylist.email)
      this.promoteActiveCard(stylist.email)
      this.expandClusterIfNeeded(stylist.email)

      // Restore form state if pending
      if (this._pendingShowRequest) {
        this._pendingShowRequest = false
        this.openConsultModal(stylist)
      }
    }

    handleResize() {
      this.state.windowWidth = window.innerWidth
      this.updateMapHost()
      if (this._updateResultsListThumb) this._updateResultsListThumb()
    }

    updateMapHost() {
      if (!this.state.mapContainer) return
      const targetHost = this.refs.mapHostDesktop

      if (targetHost && targetHost.contains(this.state.mapContainer)) {
        this.scheduleMapResize(100)
        return
      }

      if (targetHost) {
        targetHost.innerHTML = ''
        targetHost.appendChild(this.state.mapContainer)
        this.scheduleMapResize(100)
      }
    }

    createMap() {
      const mapContainer = document.createElement('div')
      mapContainer.className = 'stylist-locator__map'
      this.state.mapContainer = mapContainer

      const targetHost = this.refs.mapHostDesktop

      if (targetHost) {
        targetHost.appendChild(mapContainer)
      }

      const center = DEFAULT_CENTER || { lat: 37.1103719, lng: -113.5925891 }
      const map = createMap(mapContainer, center, DEFAULT_ZOOM_OUT || 4)
      this.state.map = map


      // Desktop-only: when a click/focus inside the Google Maps canvas tries to
      // auto-scroll the page, snap it back. Skip on touch devices — the rAF
      // loop fights native page scroll and makes the map appear to glitch.
      const isCoarsePointer =
        typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
      if (!isCoarsePointer) {
        let pendingUntil = 0
        const restoreScrollFor = (durationMs) => {
          const savedY = window.scrollY
          pendingUntil = performance.now() + durationMs
          const tick = () => {
            if (performance.now() > pendingUntil) return
            if (window.scrollY !== savedY) {
              window.scroll({ top: savedY, left: window.scrollX, behavior: 'instant' })
            }
            requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        }
        mapContainer.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch') return
          restoreScrollFor(1000)
        }, true)
        mapContainer.addEventListener('focusin', () => restoreScrollFor(1000), true)
      }

      // Initialize clusterer after map is ready
      if (initializeClusterer && globalThis.markerClusterer) {
        this.state.markerClusterer = initializeClusterer(map, (eventOrCluster) => {
          this.handleClusterClick(eventOrCluster)
        })
      }

      // Click on empty map area to deselect active stylist and close expanded clusters
      map.addListener('click', () => {
        if (this.state.meshMarkers) {
          this.clearMeshMarkers()
        }
        // Don't deselect if the consult modal is open (on mobile, map click can
        // fire after marker click due to event ordering)
        if (this.state.showRequest) return
        if (this.state.selectedStylist) {
          this.state.selectedStylist = null
          this.updateActiveCard(null)
          this.updateActiveMarker(null)
          this.restoreCardOrder()
          this.syncUrl(true)
        }
      })

      // Monitor zoom changes to update clustering mode when crossing threshold
      // Use a debounced approach to prevent excessive updates
      let zoomUpdateTimeout = null
      map.addListener('zoom_changed', () => {
        const currentZoom = map.getZoom()
        const shouldCluster = currentZoom < 24 // CLUSTERING_ZOOM_THRESHOLD

        // Update minZoom restriction: allow zooming out max 20% from current view
        if (this.state.searchCenter) {
          const minZoom = calculateZoomForRadius(
            this.getRadiusMiles() * 1.2,
            this.state.map,
            this.state.mapContainer,
            this.state.searchCenter
          )
          this.state.map.setOptions({ minZoom })
        }

        // Clear any pending updates
        if (zoomUpdateTimeout) {
          clearTimeout(zoomUpdateTimeout)
        }

        // Reposition expanded mesh markers to maintain consistent pixel spacing
        if (this.state.meshMarkers) {
          this.repositionMeshMarkers()
        }

        // Only update markers when actually crossing the clustering threshold
        if (this.state.isClusteringEnabled !== shouldCluster) {
          zoomUpdateTimeout = setTimeout(() => {
            this.state.isClusteringEnabled = shouldCluster
            this.updateMarkers()
          }, 150) // Small delay to ensure zoom has settled
        }
      })
      
      // Ensure map is in the correct host after creation
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.updateMapHost()
      }, 0)
    }

    findClusterForStylist(stylistEmail) {
      if (!this.state.markerClusterer || !this.state.markers.has(stylistEmail)) {
        return null
      }

      const stylistMarker = this.state.markers.get(stylistEmail)
      const clusters = this.state.markerClusterer.clusters || []

      for (const cluster of clusters) {
        if (cluster.markers && cluster.markers.includes(stylistMarker)) {
          return cluster
        }
      }

      return null
    }

    expandClusterIfNeeded(stylistEmail) {
      const currentZoom = this.state.map ? this.state.map.getZoom() : 0
      const shouldCluster = currentZoom < 24 // CLUSTERING_ZOOM_THRESHOLD from map.js

      if (!shouldCluster || !this.state.markerClusterer) {
        return
      }

      const cluster = this.findClusterForStylist(stylistEmail)
      if (cluster && cluster.markers && cluster.markers.length > 1) {
        // Expand the cluster but skip auto-open since we want to open the specific stylist
        this.handleClusterClick(cluster, { skipAutoOpen: true })

        // After mesh animation completes, activate the stylist's marker
        setTimeout(() => {
          if (this.state.meshMarkerMap && this.state.meshMarkerMap.has(stylistEmail)) {
            this.updateActiveMarker(stylistEmail)
          }
        }, 550)
      }
    }

    handleClusterClick(eventOrCluster, options = {}) {
      const { skipAutoOpen = false } = options

      // Handle both cases: cluster object directly or event object with cluster property
      let cluster = eventOrCluster
      if (eventOrCluster && eventOrCluster.cluster) {
        cluster = eventOrCluster.cluster
      }

      if (!cluster) {
        console.warn('StylistLocator: Invalid cluster in handleClusterClick', eventOrCluster)
        return
      }
      
      const clusterMarkers = cluster.markers || []
      
      // Get stylists from markers by matching marker instances
      const stylists = []
      for (const clusterMarker of clusterMarkers) {
        // Find stylist by matching marker instance
        for (const [email, stylistMarker] of this.state.markers.entries()) {
          if (stylistMarker === clusterMarker) {
            const stylist = this.state.stylists.find((s) => s.email === email) ||
                           this.state.cachedStylists.find((s) => s.email === email)
            if (stylist) {
              stylists.push(stylist)
            }
            break
          }
        }
      }

      if (stylists.length === 0) return

      // Define threshold for "big cluster" - if too many items, zoom in instead of expanding
      const BIG_CLUSTER_THRESHOLD = 20
      
      // If cluster is too large, zoom in instead of expanding
      if (stylists.length >= BIG_CLUSTER_THRESHOLD) {
        const google = globalThis.google
        const position = cluster.position
        let center
        
        if (position instanceof google.maps.LatLng) {
          center = { lat: position.lat(), lng: position.lng() }
        } else if (position && typeof position.lat === 'function') {
          center = { lat: position.lat(), lng: position.lng() }
        } else if (position && typeof position.lat === 'number') {
          center = { lat: position.lat, lng: position.lng }
        } else {
          return // Invalid position
        }
        
        // Calculate bounds from all stylists in the cluster to determine appropriate zoom
        const bounds = new google.maps.LatLngBounds()
        stylists.forEach((stylist) => {
          if (stylist.latitude && stylist.longitude) {
            bounds.extend(new google.maps.LatLng(stylist.latitude, stylist.longitude))
          }
        })
        
        // If we have valid bounds, fit the map to show all markers
        if (!bounds.isEmpty()) {
          // Add padding to bounds for better visibility
          this.state.map.fitBounds(bounds, {
            padding: 50,
          })
        } else {
          // Fallback: zoom in by 3 levels centered on cluster
          const currentZoom = this.state.map.getZoom()
          const newZoom = Math.min(currentZoom + 3, 15)
          centerMapOnLocation(this.state.map, center, newZoom)
        }
        
        return // Don't create star mesh for large clusters
      }

      // Remove existing mesh markers and lines
      if (this.state.meshMarkers) {
        if (removeStarMesh) {
          removeStarMesh(this.state.meshMarkers)
        }
        this.state.meshMarkers = null
        if (this.state.meshMarkerMap) {
          this.state.meshMarkerMap.clear()
          this.state.meshMarkerMap = null
        }
      }
      
      // Re-add markers to clusterer if they were temporarily removed
      if (this.state.tempRemovedMarkers && this.state.markerClusterer) {
        this.state.markerClusterer.addMarkers(this.state.tempRemovedMarkers)
        this.state.tempRemovedMarkers = null
      }

      // Create star mesh for smaller clusters
      if (createStarMesh) {
        const google = globalThis.google
        const position = cluster.position
        let center
        
        if (position instanceof google.maps.LatLng) {
          center = position
        } else if (position && typeof position.lat === 'function') {
          center = new google.maps.LatLng(position.lat(), position.lng())
        } else if (position && typeof position.lat === 'number') {
          center = new google.maps.LatLng(position.lat, position.lng)
        } else {
          return // Invalid position
        }
        
        // Hide the cluster marker by temporarily removing the cluster's markers
        // This will cause MarkerClusterer to remove the cluster marker
        // We'll store the markers to re-add them later
        if (this.state.markerClusterer && cluster.markers && cluster.markers.length > 0) {
          // Temporarily remove markers from clusterer to hide the cluster
          const tempMarkers = [...cluster.markers]
          this.state.markerClusterer.removeMarkers(tempMarkers)
          // Store markers to re-add when mesh is removed
          this.state.tempRemovedMarkers = tempMarkers
        }
        
        // Initialize mesh marker map in state BEFORE creating mesh
        // This ensures it's available when click handlers fire
        this.state.meshMarkerMap = new Map()
        
        const meshData = createStarMesh(
          this.state.map,
          center,
          stylists,
          (stylist, meshMarker) => {
            // Store mesh marker in state map
            if (this.state.meshMarkerMap) {
              this.state.meshMarkerMap.set(stylist.email, meshMarker)
            }

            // Use setTimeout to ensure marker is fully set up before handling click
            setTimeout(() => {
              this.handleMarkerClick(stylist, meshMarker)
            }, 0)
          }
        )

        // Store mesh data
        this.state.meshMarkers = meshData

        // Store all mesh markers immediately in the map for lookup
        if (meshData && meshData.markers && meshData.stylists && this.state.meshMarkerMap) {
          meshData.markers.forEach((marker, index) => {
            const stylist = meshData.stylists[index]
            if (stylist) {
              this.state.meshMarkerMap.set(stylist.email, marker)
            }
          })
        }

        // Auto-open the first stylist's card after expansion animation completes
        // Skip if this was triggered programmatically (e.g., from clicking a specific stylist in the list)
        if (!skipAutoOpen) {
          const meshStylists = meshData?.stylists || []
          const defaultStylist = meshStylists[0] || null

          if (defaultStylist) {
            setTimeout(() => {
              this.trackStylistView(defaultStylist, 'cluster_auto_open')
              this.state.selectedStylist = defaultStylist
              this.updateActiveCard(defaultStylist.email)
              this.updateActiveMarker(defaultStylist.email)
              this.promoteActiveCard(defaultStylist.email)
            }, 500) // Wait for expansion animation to complete
          }
        }
      }
    }

    initializeUserLocation() {
      initializeUserLocation(
        (coords) => {
          this.state.userCenter = coords
          this.state.referenceCenter = coords
          this.state.locationEnabled = true
          this.state.initialCenter = { ...coords }
          this.state.hasInitialized = true
          this.state.radius = this.state.initialRadius
          this.state.radiusIsDefault = true

          this.centerMap(coords, Number(this.state.initialRadius) || 25)

          if (this.state.userMarker) {
            this.state.userMarker.setMap(null)
          }
          this.state.userMarker = createUserMarker(this.state.map, coords)

          this.refreshResults()
          this.syncUrl()
        },
        () => {
          this.state.locationEnabled = false
          this.state.hasInitialized = true
          this.state.initialCenter = null
          this.updateResetButtons()
        }
      )
    }

    updateRadiusCircle() {
      const center = this.getReferenceCenter()
      const radiusMiles = Number(this.state.radius)
      this.state.radiusCircle = updateRadiusCircle(
        this.state.map,
        this.state.radiusCircle,
        radiusMiles,
        center
      )
    }

    updateMarkers() {
      const selectedEmail = this.state.selectedStylist?.email || null
      
      // Remove mesh markers when updating
      this.clearMeshMarkers()
      
      updateMarkers(
        this.state.map,
        this.state.markers,
        this.state.stylists,
        selectedEmail,
        (stylist, marker) => this.handleMarkerClick(stylist, marker),
        this.state.markerClusterer
      )
    }

    renderList() {
      // Skip full rebuild if the stylist array and the matching/non-matching
      // split haven't changed.
      const listKey = `${this.state.matchingCount ?? 'none'}|${this.state.stylists.map(s => s.email).join(',')}`
      if (listKey === this._lastRenderedListKey) return
      this._lastRenderedListKey = listKey

      const selectedEmail = this.state.selectedStylist?.email || null
      renderList(
        this.refs.listCount,
        this.refs.listDesktop,
        this.refs.listMobile,
        this.state.stylists,
        selectedEmail,
        {
          onListItemClick: (emailKey) => {
            this.handleListItemClick(emailKey)
          },
          onCardClick: (emailKey) => {
            this.handleCardClick(emailKey)
          },
        },
        this.state.matchingCount
      )

      // Setup "Learn More" expand/collapse handlers
      const { list: listModule = {} } = namespace
      if (listModule.setupLearnMoreHandlers) {
        if (this.refs.listDesktop) listModule.setupLearnMoreHandlers(this.refs.listDesktop)
        if (this.refs.listMobile) listModule.setupLearnMoreHandlers(this.refs.listMobile)
      }

      // Sync mobile results count
      if (this.refs.resultsCountMobile && this.refs.listCount) {
        this.refs.resultsCountMobile.innerHTML = this.refs.listCount.innerHTML
      }

      // Toggle gradient visibility based on number of stylists
      const listSection = this.root.querySelector('.stylist-locator__list-section')
      if (listSection) {
        if (this.state.stylists.length > 7) {
          listSection.classList.add('stylist-locator__list-section--has-gradient')
        } else {
          listSection.classList.remove('stylist-locator__list-section--has-gradient')
        }
      }

      // Recompute custom scrollbar thumb for the desktop list
      if (this._updateResultsListThumb) this._updateResultsListThumb()
    }

    /**
     * Track a stylist profile gaining focus.
     * Must be called BEFORE state.selectedStylist is reassigned so the
     * guard can skip re-firing for the already-focused stylist.
     */
    trackStylistView(stylist, viewSource) {
      if (!stylist?.email || !analytics.track) return
      if (this.state.selectedStylist?.email === stylist.email) return
      analytics.track('stylist_view', stylist.email, stylist.name, { view_source: viewSource })
    }

    handleListItemClick(emailKey) {
      const { decodeKey = (v) => v } = namespace.utils || {}
      const email = decodeKey(emailKey)
      const stylist = this.state.stylists.find((item) => item.email === email)
      if (!stylist) return

      // The "Book Consult" button opens the request form; that is tracked as
      // form_open inside openConsultModal(). click_list belongs on the results
      // list entry itself (handleCardClick), not on this button.
      this.openConsultModal(stylist)
    }

    handleCardClick(emailKey) {
      const { decodeKey = (v) => v, parseCoordinate = (v) => v } = namespace.utils || {}
      const email = decodeKey(emailKey)
      const stylist = this.state.stylists.find((item) => item.email === email)
      if (!stylist) return

      // Track results-list click, mirroring click_map on a marker click.
      if (analytics.track) {
        const position = this.state.stylists.findIndex((s) => s.email === email)
        analytics.track('click_list', email, stylist.name, { list_position: position })
      }

      this.trackStylistView(stylist, 'list_card')
      this.state.selectedStylist = stylist
      this.updateActiveCard(stylist.email)
      this.updateActiveMarker(stylist.email)

      // Zoom to stylist location on map
      if (stylist.latitude && stylist.longitude && this.state.map) {
        const coords = {
          lat: parseCoordinate(stylist.latitude),
          lng: parseCoordinate(stylist.longitude),
        }
        this.centerMap(coords)
      }

      // Expand cluster if needed
      this.expandClusterIfNeeded(email)
      this.syncUrl(true)
    }

    handleMarkerClick(stylist, marker = null) {
      // Track map marker click
      if (analytics.track && stylist?.email) {
        analytics.track('click_map', stylist.email, stylist.name)
      }

      this.trackStylistView(stylist, 'map_marker')
      this.state.selectedStylist = stylist
      this.updateActiveCard(stylist.email)
      this.updateActiveMarker(stylist.email)
      this.promoteActiveCard(stylist.email)
      this.syncUrl(true)

      // On mobile, open results drawer with active card at top
      if (window.innerWidth <= 768) {
        this.openResultsDrawer()
        this.updateActiveCard(stylist.email)
      }
    }

    updateActiveCard(email) {
      const { encodeKey = (v) => v } = namespace.utils || {}
      const emailKey = email ? encodeKey(email) : null
      const containers = [this.refs.listDesktop, this.refs.listMobile, this.refs.resultsDrawerList].filter(Boolean)
      for (const container of containers) {
        container.querySelectorAll('.stylist-locator__profile-card--active').forEach((card) => {
          card.classList.remove('stylist-locator__profile-card--active')
        })
        if (emailKey) {
          const card = container.querySelector(`[data-stylist-email="${emailKey}"]`)
          if (card) card.classList.add('stylist-locator__profile-card--active')
        }
      }
    }

    /**
     * Move the selected card to the top of each list container using a FLIP animation.
     * Cards smoothly slide into their new positions instead of scrolling.
     */
    promoteActiveCard(email) {
      if (!email) return
      const { encodeKey = (v) => v } = namespace.utils || {}
      const emailKey = encodeKey(email)
      const containers = [this.refs.listDesktop, this.refs.listMobile, this.refs.resultsDrawerList].filter(Boolean)

      for (const container of containers) {
        const card = container.querySelector(`[data-stylist-email="${emailKey}"]`)
        if (!card || card === container.firstElementChild) continue

        // FLIP: record "First" positions of all cards
        const cards = Array.from(container.querySelectorAll('.stylist-locator__profile-card'))
        const firstRects = new Map()
        cards.forEach(c => firstRects.set(c, c.getBoundingClientRect()))

        // Move card to top (DOM reorder)
        container.prepend(card)

        // Reset scroll to top so the promoted card is visible
        const scrollParent = container.parentElement && container.parentElement.scrollHeight > container.parentElement.clientHeight
          ? container.parentElement : container
        scrollParent.scrollTop = 0

        // FLIP: record "Last" positions, compute "Invert", and "Play"
        cards.forEach(c => {
          const first = firstRects.get(c)
          const last = c.getBoundingClientRect()
          const dx = first.left - last.left
          const dy = first.top - last.top

          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return

          c.style.transform = `translate(${dx}px, ${dy}px)`
          c.style.transition = 'none'

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              c.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
              c.style.transform = ''
              c.addEventListener('transitionend', function cleanup(e) {
                if (e.propertyName === 'transform') {
                  c.style.transition = ''
                  c.removeEventListener('transitionend', cleanup)
                }
              })
            })
          })
        })
      }
    }

    /**
     * Restore cards to their original order (matching state.stylists) with a FLIP animation.
     */
    restoreCardOrder() {
      const { encodeKey = (v) => v } = namespace.utils || {}
      const order = this.state.stylists.map(s => encodeKey(s.email))
      const containers = [this.refs.listDesktop, this.refs.listMobile].filter(Boolean)

      for (const container of containers) {
        const cards = Array.from(container.querySelectorAll('.stylist-locator__profile-card'))
        if (!cards.length) continue

        // Check if already in order
        const currentOrder = cards.map(c => c.dataset.stylistEmail)
        if (order.every((key, i) => currentOrder[i] === key)) continue

        // FLIP: record First positions
        const firstRects = new Map()
        cards.forEach(c => firstRects.set(c, c.getBoundingClientRect()))

        // Reorder DOM to match state.stylists
        order.forEach(emailKey => {
          const card = container.querySelector(`[data-stylist-email="${emailKey}"]`)
          if (card) container.appendChild(card)
        })

        // FLIP: animate
        cards.forEach(c => {
          const first = firstRects.get(c)
          if (!first) return
          const last = c.getBoundingClientRect()
          const dx = first.left - last.left
          const dy = first.top - last.top

          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return

          c.style.transform = `translate(${dx}px, ${dy}px)`
          c.style.transition = 'none'

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              c.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
              c.style.transform = ''
              c.addEventListener('transitionend', function cleanup(e) {
                if (e.propertyName === 'transform') {
                  c.style.transition = ''
                  c.removeEventListener('transitionend', cleanup)
                }
              })
            })
          })
        })
      }
    }

    _findMarker(email) {
      // Prefer mesh markers (expanded cluster pins) over regular markers
      return this.state.meshMarkerMap?.get(email) || this.state.markers.get(email)
    }

    updateActiveMarker(email) {
      const { buildMarkerIcon = () => null } = namespace.templates || {}
      const { determineRoleCategory = () => 'default' } = geometry

      // Reset previous active marker
      if (this._activeMarkerEmail) {
        const prevMarker = this._findMarker(this._activeMarkerEmail)
        if (prevMarker) {
          const prevStylist = this.state.stylists.find(s => s.email === this._activeMarkerEmail)
          const prevCategory = prevStylist ? determineRoleCategory(prevStylist) : 'default'
          const icon = buildMarkerIcon(prevCategory, { active: false })
          if (icon) prevMarker.setIcon(icon)
          if (this._prevMarkerZIndex != null) {
            prevMarker.setZIndex(this._prevMarkerZIndex)
            this._prevMarkerZIndex = null
          }
        }
      }

      // Set new active marker
      if (email) {
        const marker = this._findMarker(email)
        if (marker) {
          const stylist = this.state.stylists.find(s => s.email === email)
          const category = stylist ? determineRoleCategory(stylist) : 'default'
          const icon = buildMarkerIcon(category, { active: true })
          if (icon) marker.setIcon(icon)
          this._prevMarkerZIndex = marker.getZIndex()
          const google = globalThis.google
          const maxZ = (google?.maps?.Marker?.MAX_ZINDEX || 1000000)
          marker.setZIndex(maxZ + 999)
        }
      }

      this._activeMarkerEmail = email
    }

    openConsultModal(stylist) {
      if (!stylist || !this.refs.modalOverlay || !this.refs.modal) return
      // Close other drawers but NOT the consult modal itself — calling closeConsultModal()
      // here would set a 400ms timeout that clears the modal content we're about to insert
      if (this._closeFilterDrawer) this._closeFilterDrawer()
      this.closeResultsDrawer()
      if (this.root._directoryModal) this.root._directoryModal.close()

      // Track form open
      if (analytics.track && stylist.email) {
        analytics.track('form_open', stylist.email, stylist.name)
      }

      // Keep the handle, insert content after it
      const handle = this.refs.modal.querySelector('[data-element="modal-handle"]')
      if (handle) {
        handle.nextSibling && handle.parentNode.removeChild(handle.nextSibling)
        handle.insertAdjacentHTML('afterend', renderConsultModal(stylist))
      } else {
        this.refs.modal.innerHTML = renderConsultModal(stylist)
      }
      this.refs.modalOverlay.classList.add('stylist-locator__modal-overlay--open')
      this._lockBodyScroll()

      this.state.showRequest = true
      this.syncUrl(true)

      this._consultEscHandler = this._addEscapeClose(() => this.closeConsultModal())

      // Close button (desktop)
      const closeBtn = this.refs.modal.querySelector('[data-modal-close]')
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeConsultModal())
      }

      // Drag anywhere on the sheet to dismiss (app-style bottom sheet, mobile).
      // Listeners delegate from the modal element itself, so they survive the
      // content swap on each open; the util guards against double-binding
      if (namespace.utils?.attachSheetDrag) {
        namespace.utils.attachSheetDrag(this.refs.modal, () => this.closeConsultModal())
      }

      // Overlay click to close — delay listener to prevent the opening tap from
      // immediately triggering a close on mobile (touch event bubbles to overlay)
      setTimeout(() => {
        this.refs.modalOverlay.addEventListener('click', (e) => {
          if (e.target === this.refs.modalOverlay) this.closeConsultModal()
        }, { once: true })
      }, 100)

      // Phone formatting on blur
      const phoneInput = this.refs.modal.querySelector('[data-modal-phone]')
      if (phoneInput) {
        attachPhoneBlur(phoneInput)
      }

      // Form submission
      const form = this.refs.modal.querySelector('[data-modal-form]')
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault()
          this.handleModalSubmit(stylist)
        })
      }

      // Mobile submit button (outside form, triggers form submit)
      const mobileSubmit = this.refs.modal.querySelector('[data-modal-submit-mobile]')
      if (mobileSubmit && form) {
        mobileSubmit.addEventListener('click', () => {
          form.requestSubmit()
        })
      }

      // Back to stylist details button
      const backBtn = this.refs.modal.querySelector('[data-modal-back]')
      if (backBtn) {
        backBtn.addEventListener('click', () => this.closeConsultModal())
      }
    }

    async handleModalSubmit(stylist) {
      const modal = this.refs.modal
      if (!modal) return

      const nameVal = (modal.querySelector('[data-modal-name]')?.value || '').trim()
      const emailVal = (modal.querySelector('[data-modal-email]')?.value || '').trim()
      const phoneVal = (modal.querySelector('[data-modal-phone]')?.value || '').trim()
      const howVal = (modal.querySelector('[data-modal-how-did-you-hear]')?.value || '').trim()

      // Validation
      if (!nameVal) { this.showRequestError('Please enter your name'); return }
      if (!emailVal) { this.showRequestError('Please enter your email'); return }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(emailVal)) { this.showRequestError('Please enter a valid email address'); return }
      if (!isValidPhone(phoneVal)) { this.showRequestError('Please enter a valid phone number (e.g., (555) 123-4567 or +44 20 1234 5678)'); return }
      if (!howVal) { this.showRequestError('Please tell us how you heard about us'); return }

      const submitBtn = modal.querySelector('[data-modal-submit]')
      if (submitBtn) submitBtn.disabled = true

      this.toastManager.show('request', {
        message: 'Sending request...',
        severity: 'info',
        duration: Infinity,
      })

      const appointmentEndpoint = this.getAppointmentEndpoint()
      if (!appointmentEndpoint) {
        this.toastManager.remove('request')
        this.toastManager.show('request', { message: 'API endpoint not configured', severity: 'error', duration: 3000 })
        if (submitBtn) submitBtn.disabled = false
        return
      }

      try {
        const response = await fetch(appointmentEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
          body: JSON.stringify({
            name: nameVal,
            email: emailVal,
            phone: phoneVal,
            howDidYouHear: howVal,
            stylistEmail: stylist.email,
            stylistName: stylist.name,
          }),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to send request' }))
          throw new Error(error.details || error.error || 'Failed to send request')
        }

        await response.json()

        if (analytics.track && stylist.email) {
          analytics.track('form_submit', stylist.email, stylist.name, {
            success: true,
            how_did_you_hear: howVal,
          })
        }

        this.closeConsultModal()
        this.toastManager.remove('request')
        this.toastManager.show('request', {
          message: 'Request Sent!',
          severity: 'success',
          duration: 3000,
        })

        // Redirect to thank you page
        const thankYouPage = this.root.dataset?.thankYouPage
        if (thankYouPage) {
          setTimeout(() => { window.location.href = thankYouPage }, 1500)
        }
      } catch (error) {
        console.error('Appointment request error:', error)
        this.toastManager.remove('request')
        this.toastManager.show('request', {
          message: error.message || 'Failed to send request',
          severity: 'error',
          duration: 5000,
        })
        if (submitBtn) submitBtn.disabled = false
      }
    }

    applyFilters() {
      // Radius defines "your area" — stylists outside it are still hidden.
      const inArea = filterStylistsByRadius(
        this.state.cachedStylists || [],
        Number(this.state.radius),
        this.getReferenceCenter()
      )

      // Preference filters (certification + specializations + service options)
      // now split the in-area pool into matching and non-matching groups
      // instead of hiding non-matching stylists.
      const hasPreferenceFilters =
        this.state.certification !== 'all' ||
        this.state.activeSpecializations.length > 0 ||
        this.state.activeServiceOptions.length > 0

      const matchesCertification = (stylist) =>
        filterStylistsByCertification([stylist], this.state.certification).length === 1

      const matchesSpecializations = (stylist) => {
        if (!this.state.activeSpecializations.length) return true
        const specs = stylist.specializations || []
        return this.state.activeSpecializations.some(f => specs.includes(f))
      }

      const matchesServiceOptions = (stylist) => {
        if (!this.state.activeServiceOptions.length) return true
        const opts = stylist.service_options || []
        return this.state.activeServiceOptions.some(f => opts.includes(f))
      }

      const matchesAllPreferences = (stylist) =>
        matchesCertification(stylist) &&
        matchesSpecializations(stylist) &&
        matchesServiceOptions(stylist)

      const matching = []
      const nonMatching = []
      for (const stylist of inArea) {
        if (matchesAllPreferences(stylist)) {
          matching.push(stylist)
        } else {
          nonMatching.push(stylist)
        }
      }

      // Sort stylists based on activeSort setting
      const rolePriority = { 'elite': 1, 'masters': 2, 'certified_stylist': 3, 'default': 4 }
      const roleNameToCategory = {
        'Elite Artist': 'elite',
        'Master Technician': 'masters',
        'Certified Stylist': 'certified_stylist',
      }

      const getSortPriority = (stylist) => {
        let cat = stylist.roleCategory || determineRoleCategory(stylist) || 'default'
        if (cat === 'certified_salon' && stylist.role) {
          cat = roleNameToCategory[stylist.role] || 'certified_stylist'
        }
        return rolePriority[cat] || 999
      }

      const referenceCenter = this.getReferenceCenter()

      const getDistance = (stylist) => {
        if (!referenceCenter || !stylist.latitude || !stylist.longitude) return Infinity
        return computeDistanceMeters(referenceCenter.lat, referenceCenter.lng, stylist.latitude, stylist.longitude)
      }

      // Boost stylists whose name or salon name matches the search query so
      // typing "Mendy Nielsen" surfaces Mendy at the top of the list regardless
      // of cert tier or distance.
      const searchQuery = (this.state.search || '').trim().toLowerCase()
      const getNameMatchRank = (stylist) => {
        if (!searchQuery) return 3
        const name = (stylist.name || '').toLowerCase()
        const salon = (stylist.salonName || stylist.salon_name || stylist.business_name || '').toLowerCase()
        if (name === searchQuery || salon === searchQuery) return 0
        if (name.startsWith(searchQuery) || salon.startsWith(searchQuery)) return 1
        if (name.includes(searchQuery) || salon.includes(searchQuery)) return 2
        return 3
      }

      const compareStylists = (a, b) => {
        const aMatch = getNameMatchRank(a)
        const bMatch = getNameMatchRank(b)
        if (aMatch !== bMatch) return aMatch - bMatch

        const aPriority = getSortPriority(a)
        const bPriority = getSortPriority(b)

        switch (this.state.activeSort) {
          case 'nearest': {
            const aDist = getDistance(a)
            const bDist = getDistance(b)
            if (aDist !== bDist) return aDist - bDist
            if (aPriority !== bPriority) return aPriority - bPriority
            break
          }
          case 'highest_education': {
            if (aPriority !== bPriority) return aPriority - bPriority
            break
          }
          default: { // best_match
            if (aPriority !== bPriority) return aPriority - bPriority
            const aDist = getDistance(a)
            const bDist = getDistance(b)
            if (aDist !== bDist) return aDist - bDist
            break
          }
        }

        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      }

      matching.sort(compareStylists)
      nonMatching.sort(compareStylists)

      const combined = [...matching, ...nonMatching]
      // `null` signals "no preference filters active" so the list module
      // renders today's single-section view with no separator.
      const matchingCount = hasPreferenceFilters ? matching.length : null

      if (
        this.state.selectedStylist &&
        !combined.find((stylist) => stylist.email === this.state.selectedStylist.email)
      ) {
        this.clearSelection()
      }
      this.setStylists(combined, { matchingCount })
      this.renderFilterChips()
    }

    getLocationEndpoint() {
      return this.buildEndpoint('/api/search-customers-db')
    }

    getAppointmentEndpoint() {
      return this.buildEndpoint('/api/appointment-request')
    }

    fetchStylistsForCurrentRadius() {
      const locationEndpoint = this.getLocationEndpoint()
      if (!locationEndpoint) return Promise.resolve()
      const center = this.getReferenceCenter()
      const radiusMiles = Number(this.state.radius) || Number(this.defaultRadius) || 25
      if (!center || !radiusMiles || Number.isNaN(radiusMiles)) {
        return Promise.resolve()
      }
      const bounds = computeBoundsFromCenter(center, radiusMiles)
      return this.loadStylists(bounds)
    }

    async loadStylists(bounds) {
      if (this.state.isLoading) return
      this.state.isLoading = true

      const locationEndpoint = this.getLocationEndpoint()
      const stylists = await loadStylists(locationEndpoint, bounds, this.toastManager)
      this.state.cachedStylists = filterStylistsByValidRoles(stylists)
      this.state.isLoading = false
      this.applyFilters()
    }

    getReferenceCenter() {
      if (this.state.referenceCenter) {
        return this.state.referenceCenter
      }
      if (this.state.searchCenter) {
        return this.state.searchCenter
      }
      if (this.state.userCenter) {
        return this.state.userCenter
      }
      return null
    }

    updateResetButtons() {
      const shouldShow = hasActiveFilters(this.state)
      this.refs.resetMapButtons.forEach((button) => {
        if (!button) return
        if (shouldShow) {
          button.classList.add('is-visible')
        } else {
          button.classList.remove('is-visible')
        }
      })
    }

    renderViewToggle() {
      this.refs.viewToggleButtons.forEach((button) => {
        const view = button.dataset.view
        if (view === this.state.viewMode) {
          button.classList.add('stylist-locator__select-button--active')
        } else {
          button.classList.remove('stylist-locator__select-button--active')
        }
      })

      // Update mobile layout data attribute for CSS-driven visibility
      if (this.refs.mobileLayout) {
        this.refs.mobileLayout.dataset.viewMode = this.state.viewMode
      }
    }


    renderFilterChips() {
      if (!this.refs.filterChips) return

      const chips = []
      
      // Certification chip
      if (!this.state.certificationIsDefault && this.state.certification !== 'all') {
        const certificationLabel = this.getCertificationLabel(this.state.certification)
        if (certificationLabel) {
          chips.push({
            type: 'certification',
            label: certificationLabel,
            icon: this.getCertificationIcon(this.state.certification),
          })
        }
      }

      // Radius chip
      if (!this.state.radiusIsDefault && this.state.radius) {
        const radiusLabel = getRadiusLabel(this.state, {
          getRadiusSelect: () => this.refs.radiusSelects[0],
        })
        if (radiusLabel && radiusLabel.toLowerCase() !== 'radius') {
          chips.push({
            type: 'radius',
            label: radiusLabel,
          })
        }
      }

      // Render chips
      if (chips.length === 0) {
        this.refs.filterChips.innerHTML = ''
        if (this.refs.filterActiveChips) this.refs.filterActiveChips.innerHTML = ''
        return
      }

      this.refs.filterChips.innerHTML = chips.map((chip) => {
        const iconHtml = chip.icon
          ? `<span class="stylist-locator__filter-chip-icon" aria-hidden="true">${chip.icon}</span>`
          : ''
        
        return `
          <div class="stylist-locator__filter-chip" data-filter-type="${chip.type}">
            <div class="stylist-locator__filter-chip-content">
              ${iconHtml}
              <span class="stylist-locator__filter-chip-text">${chip.label}</span>
            </div>
            <button type="button" class="stylist-locator__filter-chip-close" data-filter-type="${chip.type}" aria-label="Remove ${chip.label} filter">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <mask id="mask0_5459_173" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                  <path d="M4.75 1C6.82083 1 8.5 2.67917 8.5 4.75C8.5 6.82083 6.82083 8.5 4.75 8.5C2.67917 8.5 1 6.82083 1 4.75C1 2.67917 2.67917 1 4.75 1Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.7526 4.74967L6.41927 6.41634M4.7526 4.74967L3.08594 3.08301M4.7526 4.74967L3.08594 6.41634M4.7526 4.74967L6.41927 3.08301" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </mask>
                <g mask="url(#mask0_5459_173)">
                  <path d="M9.75 -0.25H-0.25V9.75H9.75V-0.25Z" fill="#B4B4B4"/>
                </g>
              </svg>
            </button>
          </div>
        `
      }).join('')

      // Sync to mobile active chips area
      if (this.refs.filterActiveChips) {
        this.refs.filterActiveChips.innerHTML = chips.map((chip) => `
          <div class="stylist-locator__active-filter-pill" data-filter-type="${chip.type}">
            <span>${chip.label}</span>
            <button type="button" class="stylist-locator__active-filter-pill-close" data-filter-type="${chip.type}" aria-label="Remove ${chip.label}">
              <svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M1 1L6 6M6 1L1 6" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>
            </button>
          </div>
        `).join('') + '<button type="button" class="stylist-locator__active-filter-clear-all">Clear all</button>'

        this.refs.filterActiveChips.querySelectorAll('.stylist-locator__active-filter-pill-close').forEach((btn) => {
          btn.addEventListener('click', () => {
            const filterType = btn.dataset.filterType
            if (filterType === 'certification' && this.refs.certificationSelect) {
              this.refs.certificationSelect.value = 'all'
              this.refs.certificationSelect.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        })

        const clearAll = this.refs.filterActiveChips.querySelector('.stylist-locator__active-filter-clear-all')
        if (clearAll) {
          clearAll.addEventListener('click', () => {
            if (this.refs.certificationSelect) {
              this.refs.certificationSelect.value = 'all'
              this.refs.certificationSelect.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        }
      }

      // Add click handlers
      this.refs.filterChips.querySelectorAll('.stylist-locator__filter-chip-close').forEach((button) => {
        button.addEventListener('click', () => {
          const filterType = button.dataset.filterType
          if (filterType === 'certification') {
            // Reset to 'all'
            this.refs.certificationRadios.forEach((radio) => {
              if (radio.value === 'all') {
                radio.checked = true
                radio.dispatchEvent(new Event('change', { bubbles: true }))
              }
            })
          } else if (filterType === 'radius') {
            // Reset radius to default
            this.refs.radiusSelects.forEach((select) => {
              select.selectedIndex = 0
              select.dispatchEvent(new Event('change', { bubbles: true }))
            })
          }
        })
      })
    }

    getCertificationLabel(value) {
      const radio = this.refs.certificationRadios.find((r) => r.value === value)
      if (!radio) return null
      const label = radio.closest('.stylist-locator__radio')?.querySelector('label')
      return label ? label.textContent.trim() : null
    }

    getCertificationIcon(value) {
      // Return SVG icon based on certification type - matching the icons used in the filter options
      if (value === 'certified_salon') {
        // Star icon for certified salon (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 18 17" fill="none">
          <path d="M8.56088 13.7L4.41088 16.2C4.22754 16.3167 4.03588 16.3667 3.83588 16.35C3.63588 16.3333 3.46088 16.2667 3.31088 16.15C3.16088 16.0333 3.04421 15.8877 2.96088 15.713C2.87754 15.5383 2.86088 15.3423 2.91088 15.125L4.01088 10.4L0.335876 7.225C0.169209 7.075 0.0652091 6.904 0.0238758 6.712C-0.0174575 6.52 -0.00512426 6.33267 0.0608757 6.15C0.126876 5.96733 0.226876 5.81733 0.360876 5.7C0.494876 5.58267 0.678209 5.50767 0.910876 5.475L5.76088 5.05L7.63588 0.6C7.71921 0.4 7.84854 0.25 8.02388 0.15C8.19921 0.0499998 8.37821 0 8.56088 0C8.74354 0 8.92254 0.0499998 9.09788 0.15C9.27321 0.25 9.40254 0.4 9.48588 0.6L11.3609 5.05L16.2109 5.475C16.4442 5.50833 16.6275 5.58333 16.7609 5.7C16.8942 5.81667 16.9942 5.96667 17.0609 6.15C17.1275 6.33333 17.1402 6.521 17.0989 6.713C17.0575 6.905 16.9532 7.07567 16.7859 7.225L13.1109 10.4L14.2109 15.125C14.2609 15.3417 14.2442 15.5377 14.1609 15.713C14.0775 15.8883 13.9609 16.034 13.8109 16.15C13.6609 16.266 13.4859 16.3327 13.2859 16.35C13.0859 16.3673 12.8942 16.3173 12.7109 16.2L8.56088 13.7Z" fill="#C6AC78"/>
        </svg>`
      } else if (value === 'elite') {
        // Location pin icon for elite (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 1.00146e-08C5.89206 -0.00010539 3.86926 0.831759 2.37124 2.31479C0.873231 3.79782 0.0210794 5.81216 0 7.92C0 13.4 7.05 19.5 7.35 19.76C7.53113 19.9149 7.76165 20.0001 8 20.0001C8.23835 20.0001 8.46887 19.9149 8.65 19.76C9 19.5 16 13.4 16 7.92C15.9789 5.81216 15.1268 3.79782 13.6288 2.31479C12.1307 0.831759 10.1079 -0.00010539 8 1.00146e-08ZM8 11C7.30777 11 6.63108 10.7947 6.0555 10.4101C5.47993 10.0256 5.03133 9.47893 4.76642 8.83939C4.50151 8.19985 4.4322 7.49612 4.56725 6.81718C4.7023 6.13825 5.03564 5.51461 5.52513 5.02513C6.01461 4.53564 6.63825 4.2023 7.31718 4.06725C7.99612 3.9322 8.69985 4.00151 9.33939 4.26642C9.97893 4.53133 10.5256 4.97993 10.9101 5.5555C11.2947 6.13108 11.5 6.80777 11.5 7.5C11.5 8.42826 11.1313 9.3185 10.4749 9.97487C9.8185 10.6313 8.92826 11 8 11Z" fill="currentColor"/>
        </svg>`
      } else if (value === 'masters') {
        // Location pin icon with stroke for masters (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 0.25C10.0421 0.249898 12.0019 1.0555 13.4531 2.49219C14.8137 3.83913 15.6235 5.63877 15.7363 7.54102L15.75 7.92285C15.7488 10.5704 14.0492 13.4058 12.2607 15.6416C10.4784 17.8698 8.65621 19.4443 8.50098 19.5596L8.49414 19.5645L8.4873 19.5703C8.3515 19.6864 8.17865 19.75 8 19.75C7.86605 19.75 7.73533 19.7143 7.62109 19.6475L7.5127 19.5703C7.35982 19.4378 5.53997 17.8609 3.75781 15.6406C1.96283 13.4044 0.251228 10.5698 0.25 7.92285C0.270421 5.88088 1.09568 3.92887 2.54688 2.49219C3.90737 1.1453 5.71496 0.353484 7.61816 0.259766L8 0.25ZM9.43555 4.03516C8.75035 3.75134 7.99595 3.6776 7.26855 3.82227C6.54113 3.96696 5.87308 4.32419 5.34863 4.84863C4.82419 5.37308 4.46696 6.04113 4.32227 6.76855C4.1776 7.49595 4.25134 8.25035 4.53516 8.93555C4.819 9.62064 5.3004 10.2062 5.91699 10.6182C6.5336 11.0301 7.25845 11.25 8 11.25C8.99456 11.25 9.94811 10.8546 10.6514 10.1514C11.3546 9.44811 11.75 8.49456 11.75 7.5C11.75 6.75845 11.5301 6.0336 11.1182 5.41699C10.7062 4.8004 10.1206 4.319 9.43555 4.03516Z" fill="white" stroke="currentColor" stroke-width="0.5"/>
        </svg>`
      } else if (value === 'certified_stylist') {
        // Location pin icon for certified stylist (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 1.00146e-08C5.89206 -0.00010539 3.86926 0.831759 2.37124 2.31479C0.873231 3.79782 0.0210794 5.81216 0 7.92C0 13.4 7.05 19.5 7.35 19.76C7.53113 19.9149 7.76165 20.0001 8 20.0001C8.23835 20.0001 8.46887 19.9149 8.65 19.76C9 19.5 16 13.4 16 7.92C15.9789 5.81216 15.1268 3.79782 13.6288 2.31479C12.1307 0.831759 10.1079 -0.00010539 8 1.00146e-08ZM8 11C7.30777 11 6.63108 10.7947 6.0555 10.4101C5.47993 10.0256 5.03133 9.47893 4.76642 8.83939C4.50151 8.19985 4.4322 7.49612 4.56725 6.81718C4.7023 6.13825 5.03564 5.51461 5.52513 5.02513C6.01461 4.53564 6.63825 4.2023 7.31718 4.06725C7.99612 3.9322 8.69985 4.00151 9.33939 4.26642C9.97893 4.53133 10.5256 4.97993 10.9101 5.5555C11.2947 6.13108 11.5 6.80777 11.5 7.5C11.5 8.42826 11.1313 9.3185 10.4749 9.97487C9.8185 10.6313 8.92826 11 8 11Z" fill="#798694"/>
        </svg>`
      }
      return null
    }


    destroy() {
      window.removeEventListener('resize', this.boundResizeHandler)
      if (this.galleryCleanups && this.galleryCleanups.size) {
        Array.from(this.galleryCleanups).forEach((cleanup) => {
          try {
            cleanup()
          } catch (error) {
            console.error('Stylist Locator: gallery cleanup failed', error)
          }
        })
        this.galleryCleanups.clear()
      }
      this.toastManager.clear()
      
      // Clean up mesh markers
      this.clearMeshMarkers()
      
      // Clean up clusterer
      if (this.state.markerClusterer) {
        try {
          this.state.markerClusterer.clearMarkers()
        } catch (error) {
          console.error('Stylist Locator: clusterer cleanup failed', error)
        }
        this.state.markerClusterer = null
      }
      
      if (this.state.map) {
        globalThis.google.maps.event.clearListeners(this.state.map, 'zoom_changed')
      }
      if (this.state.mapContainer && this.state.mapContainer.parentNode) {
        this.state.mapContainer.parentNode.removeChild(this.state.mapContainer)
      }
      if (this.state.radiusCircle) {
        this.state.radiusCircle.setMap(null)
        this.state.radiusCircle = null
      }
      this.closeConsultModal()
      if (this.boundDocumentClick) {
        document.removeEventListener('click', this.boundDocumentClick)
        this.boundDocumentClick = null
      }
    }
  }

  function initSection(root) {
    if (controllers.has(root)) {
      return controllers.get(root)
    }
    const controller = new StylistLocatorController(root)
    controllers.set(root, controller)
    root._stylistLocatorController = controller
    return controller
  }

  function destroySection(root) {
    const controller = controllers.get(root)
    if (controller) {
      controller.destroy()
      controllers.delete(root)
    }
  }

  function initAll() {
    document.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => initSection(element))
  }

  document.addEventListener('DOMContentLoaded', initAll)

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.detail.section
    if (!container) return
    container.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => initSection(element))
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target
    if (!container) return
    container.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => destroySection(element))
  })
})()
